// NPM Imports
import React, {
   useCallback,
   useEffect,
   useRef,
   useState,
   createContext,
} from 'react'

// CSS
import './App.css'

// Data Functions
import { fetchOpportunityProducts, fetchPayrollInfoRecords } from './js/data.js'

// React Components
import {
   ToolbarSection,
   MonthlySection,
   OneTimeSection,
   ExpansionSection,
   TotalsSection,
   UserRoleAndViewSection,
   ResellerSection,
   OtherOptionsSection,
} from './components/sections'
import { useAuthContext } from './context/AuthContext'
import useFeaturePermissions from './hooks/useFeaturePermissions'
import {
   useIntegrationObjects,
   useTime,
   useDealDeskDiscounts,
} from './hooks/queries/onMountQueries'
import {
   useGetOpportunity,
   useGetPricebooks,
   useGetCurrency,
   useGetProducts,
   useGetPrevProducts,
   useGetDiscounts,
   useTermLengths,
   usePrepayLengths,
   useGetPricingAdjustments,
} from './hooks/queries/OnDemandQueries'
import {
   LoadingScreen,
   UnauthorizedScreen,
   NoPricebooksAssigned,
} from './components/rows/misc'
import BookedQuoteNotification from './components/rows/misc/BookedQuoteNotification'
import VolumeDiscountAlert from './components/rows/misc/VolumeDiscountAlert'
import { BambooHRLogo } from './components/ui-helper-elements'
import QuoteInfoRow from './components/rows/toolbar/QuoteInfoRow'
import toast, { Toaster } from 'react-hot-toast'
import useLocalization from './hooks/useLocalization'

// Javascript Files
//@ts-ignore
import calculateQuote from './common/calculate'
import type {
   ProductState,
   DiscountState,
   ProductEINIntegration,
   AccountSnowflakeData,
   Opportunity,
   PromotionalDiscounts,
   TimeClockDetails,
   SimpleOption,
   Discount,
   ServiceDetails,
   PayrollInfo,
   DealDeskDiscount,
} from './types/types'
//@ts-ignore
import { findObjectByKeyValue } from './common/helpers.mjs'
import { useSearchParams } from 'react-router-dom'
import { removeAllPromoDiscountForProduct } from './js/validate'
import {
   extractFamilyKeys,
   mergePricingAdjustments,
} from './utils/pricingAdjustmentMerge'
import { decompressJsonForBrowser } from './utils/helpers'
import {
   resolveInitialDates,
   resolveNextInvoiceDate,
} from './utils/dateDefaults'
import { resolveInitialDifferenceQuote } from './utils/differenceQuoteDefaults'
import {
   convertDealDeskDiscountToStandardFormat,
   calculateDealDeskLocks,
   removeAllDealDeskDiscountsFromList,
} from './utils/dealDeskDiscountConverter'

/**
 * Safely parses EINs__c data from Salesforce that contains apostrophes in strings
 * @param {string} str - The stringified array of arrays with single quotes
 * @returns {Array} - Parsed array of arrays
 */
function parseEINsData(str: string): Array<Array<string>> {
   try {
      if (!str) return []

      // First, replace the string with a JSON-compatible format
      // Step 1: Temporarily replace all single quotes with a placeholder
      let processed = str.replace(/'/g, '###QUOTE###')

      // Step 2: Replace the placeholders that should be double quotes
      processed = processed
         .replace(/\[###QUOTE###/g, '["')
         .replace(/###QUOTE###\]/g, '"]')
         .replace(/###QUOTE###,###QUOTE###/g, '","')

      // Step 3: Replace remaining placeholders back to single quotes (apostrophes)
      processed = processed.replace(/###QUOTE###/g, "'")

      // Now parse the JSON
      return JSON.parse(processed)
   } catch (error) {
      console.error('Error parsing EINs__c:', error)
      return [] // Fallback to empty array
   }
}

// Context for component states
export const QuoteContext = createContext<any>(null)

const isFrozenStage = (s: string) =>
   s === 'booked' || s === 'closed won' || s === 'closed lost'

const App = () => {
   /////////////////////////////////////
   // Set host url based on the environment file .env.<env> file. (.env.prod, .env.stage, .env.dev)
   /////////////////////////////////////
   const host_url: string = process.env.REACT_APP_HOST_URL || ''
   const environment: string = process.env.REACT_APP_ENVIRONMENT || ''
   const referrer: string = document.referrer

   /////////////////////////////////////
   // Set Product and Discount Variables
   /////////////////////////////////////
   const [product_list, setproduct_list] = useState<ProductState>({})
   const [discount_list, setdiscount_list] = useState<DiscountState>({})

   /////////////////////////////////////
   // Set Strings Variable for Localization
   /////////////////////////////////////
   const {
      strings,
      getString,
      getFormattedString,
      isLoading: isLoadingStrings,
   } = useLocalization(host_url)

   ///////////////////////////////////
   // Set Query Paramters as Variables
   ///////////////////////////////////
   const [searchParams, setSearchParams] = useSearchParams()

   ///////////////////////////////////
   // Set Before Render Variables
   ///////////////////////////////////
   const { authStatus, isAuthLoading } = useAuthContext()
   const {
      featurePermissions,
      hasPermission,
      isLoading: isLoadingPermissions,
   } = useFeaturePermissions()
   const { data: integrationObjects } = useIntegrationObjects()
   const { data: timeData, isLoading: isLoadingTimeData } = useTime()

   const [integrationTypes, setIntegrationTypes] = useState([])
   const [integrationSolutions, setIntegrationSolutions] = useState([])

   // set query parameters as variables
   const [opportunity_id, setopportunity_id] = useState(
      searchParams.get('opportunity_id') || '',
   )
   const {
      data: opportunityData,
      isLoading: isLoadingOpportunity,
      error: opportunityError,
   } = useGetOpportunity(opportunity_id)
   const { data: pricebooksData, isLoading: isLoadingPricebooks } =
      useGetPricebooks(
         opportunity_id,
         searchParams.get('opportunity_created_date') || '',
      )

   const {
      data: dealDeskDiscountsData,
      isLoading: isLoadingDealDeskDiscounts,
   } = useDealDeskDiscounts(opportunity_id)
   const [parent_opportunity_id, setparent_opportunity_id] = useState('')
   const [account_name, setaccount_name] = useState('')
   const [opportunity_name, setopportunity_name] = useState('')
   const [opportunity_contact_name, setopportunity_contact_name] = useState('')
   const [opportunity_contact_email, setopportunity_contact_email] =
      useState('')
   const [last_agent_email, setlast_agent_email] = useState('')
   const [last_agent_name, setlast_agent_name] = useState('')
   const [stage, setstage] = useState('')
   const [companyId, setCompanyId] = useState('')

   const [user_view, setuser_view] = useState(() => {
      // Not the best way to redirect. Needs replaced.
      let selected_user_view = searchParams.get('user_view')
      // Checks is user_view is set. If not, return "Internal"
      if (selected_user_view != null) return selected_user_view
      else return 'Internal'
   })
   // list of user views
   const user_views = [
      { value: 'Internal', name: 'Internal' },
      { value: 'External', name: 'External' },
   ]

   // list of quote user roles
   const user_roles = [
      { value: 'Super Admin', name: 'Super Admin' },
      { value: 'Admin', name: 'Admin' },
      { value: 'Sales', name: 'Sales' },
      { value: 'Expansion', name: 'Expansion' },
      {
         value: 'Expansion Ongoing Services',
         name: 'Expansion Ongoing Services',
      },
      { value: 'Reseller', name: 'Reseller' },
      { value: 'Partner', name: 'Partner' },
      { value: 'Payroll', name: 'Payroll' },
      { value: 'Sales Manager', name: 'Sales Manager' },
      { value: 'Expansion Manager', name: 'Expansion Manager' },
      {
         value: 'Expansion Manager Ongoing Services',
         name: 'Expansion Manager Ongoing Services',
      },
      { value: 'Partner Sales', name: 'Partner Sales' },
      { value: 'Billing', name: 'Billing' },
      { value: 'HR Support', name: 'HR Support' },
      { value: 'Support Manager', name: 'Support Manager' },
      { value: 'Payroll Support', name: 'Payroll Support' },
      { value: 'Implementation', name: 'Implementation' },
      { value: 'Implementation Manager', name: 'Implementation Manager' },
      { value: 'GES Expansion', name: 'GES Expansion' },
      { value: 'GES Manager', name: 'GES Manager' },
      { value: 'EMEA Sales', name: 'EMEA Sales' },
      { value: 'EMEA Expansion', name: 'EMEA Expansion' },
      { value: 'EMEA Sales Manager', name: 'EMEA Sales Manager' },
      { value: 'EMEA Expansion Manager', name: 'EMEA Expansion Manager' },
      { value: 'CarrierConnectionImpRep', name: 'CarrierConnectionImpRep' },
   ]

   // list sf user roles
   const [user_role, setuser_role] = useState<string>(() => {
      // Not the best way to redirect. Needs replaced.
      let selected_user_role = searchParams.get('agent_role')
      // Checks is user_view is set. If not, return "Internal"
      if (selected_user_role != null) return selected_user_role
      else if (
         referrer.includes('force.com') ||
         referrer.includes('quote.bamboohr.net')
      ) {
         return 'Sales'
      } else return 'Partner'
   })

   // state for displayed pricebook
   const [pricebookId, setPricebookId] = useState<number | null>(null)
   // list of selectable pricebooks
   const [pricebooks, setPricebooks] = useState([
      { value: 0, name: '', currencyCode: 'USD' },
   ])

   const [pricebookSelectorList, setPricebookSelectorList] = useState<
      React.ReactNode[]
   >([])

   // state for display currency
   const [currencyCode, setCurrencyCode] = useState<string>('USD')
   // This is the local existing currency of the account billed in Zuora. It is set from the Account object in SF.
   const [previousCurrencyCode, setPreviousCurrencyCode] =
      useState<string>('USD')
   // state for currency Object
   const [currencyObject, setCurrencyObject] = useState({})
   const [currencyObjectPrevious, setCurrencyObjectPrevious] = useState({})
   const [currencyObjectUSD, setCurrencyObjectUSD] = useState({})

   // Fetch currency data using hooks
   const { data: currencyData, isLoading: isLoadingCurrency } =
      useGetCurrency(currencyCode)
   const { data: currencyDataUSD, isLoading: isLoadingCurrencyUSD } =
      useGetCurrency('USD')
   const { data: currencyDataPrevious, isLoading: isLoadingCurrencyPrevious } =
      useGetCurrency(previousCurrencyCode)

   // Fetch products data using hook
   const { data: productsData, isLoading: isLoadingProducts } = useGetProducts(
      pricebookId ? String(pricebookId) : '',
      currencyCode,
      user_role,
   )

   /////////////////////
   // Input Field States
   /////////////////////
   // state for which core product is selected
   const [coreProductSelected, setCoreProductSelected] = useState('core2')
   // function to handle change of core product
   const onCoreOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newCoreOption = e.target.value
      setCoreProductSelected(newCoreOption)
      if (newCoreOption === 'core0') {
         const currEECount = productCoreEECount
         setPrevCoreEECount(currEECount)
         setProductCoreEECount(0)
         removeAllPromoDiscountForProduct(
            'payrollImp',
            discount_list,
            promotionalDiscounts,
            setPromotionalDiscounts,
         )
         setImpType('selfSetup')
      } else if (prevCoreEECount > 0) {
         onProductCoreEECountChange(prevCoreEECount, newCoreOption)
      }
   }

   // state for core product
   const [productCoreEECount, setProductCoreEECount] = useState(0)
   const onProductCoreEECountChange = (
      newEECount: number,
      coreProduct: string = coreProductSelected,
   ) => {
      let prevEECount = productCoreEECount
      // Updates discounts that depend on the product count
      onMainProductChange(coreProduct, newEECount)
      setProductCoreEECount(newEECount)
      //NOTE: Should this have an additional condition of !(user_role == "Expansion" || user_role == "Partner Sales" || user_role == "Admin")
      if (prevEECount < 1 && newEECount > 0) {
         if (
            newEECount > product_list[`${coreProduct}Quickstart`]['EmpHigh']!
         ) {
            setImpType('full')
         } else {
            setImpType('quickstart')
         }
      } else if (prevEECount > 0 && newEECount < 1) {
         setImpType('selfSetup')
         removeAllPromoDiscountForProduct(
            'payrollImp',
            discount_list,
            promotionalDiscounts,
            setPromotionalDiscounts,
         )
      } else if (
         prevEECount <= product_list[`${coreProduct}Quickstart`]['EmpHigh']! &&
         newEECount > product_list[`${coreProduct}Quickstart`]['EmpHigh']!
      ) {
         setImpType('full')
      }
   }

   const onProductCoreInputBlur = () => {
      let prevEECount = productCoreEECount
      if (prevEECount < 1) {
         setProductCoreEECount(0)
      } else {
         setProductCoreEECount(Math.floor(prevEECount))
      }
   }
   // state for performance product
   const [productPerformanceEECount, setProductPerformanceEECount] =
      useState<number>(0)

   /////////////////////
   // Time Related Products
   /////////////////////
   // State for Time Related Products
   const [timeProductSelected, setTimeProductSelected] =
      useState<string>('timeAttendance')
   // Function to handle the change of the time product option
   const onTimeProductOptionChange = (
      e: React.ChangeEvent<HTMLInputElement>,
   ) => {
      setTimeProductSelected(e.target.value)
   }

   // state for time tracking product and clocks
   const [productTimeTrackingEECount, setProductTimeTrackingEECount] =
      useState<number>(0)
   const [productHasTimeClocks, setProductHasTimeClocks] =
      useState<boolean>(false)
   const [productTimeClockDetails, setProductTimeClockDetails] = useState<
      TimeClockDetails[]
   >([])

   // state for benefits administration product
   const [
      productBenefitsAdministrationEECount,
      setProductBenefitsAdministrationEECount,
   ] = useState<number>(0)
   const onProductBenefitsAdministrationEECountChange = (
      newEECount: number,
   ) => {
      let prevEECount = productBenefitsAdministrationEECount
      // Updates discounts that depend on the product count
      onMainProductChange('benAdmin', newEECount)

      setProductBenefitsAdministrationEECount(newEECount)
      //NOTE: Should this have an additional condition of !(user_role == "Expansion" || user_role == "Partner Sales" || user_role == "Admin")
      if (prevEECount < 1 && newEECount > 0) {
         setBenAdminImpType('full')
      } else if (prevEECount > 0 && newEECount < 1) {
         setBenAdminImpType('none')
      }
   }

   // Multi EIN Varibales
   const [productPayrollEINCount, setProductPayrollEINCount] = useState(0)
   const [productPayrollEINNames, setProductPayrollEINNames] = useState<
      string[]
   >([])
   const [productPayrollEECounts, setProductPayrollEECounts] = useState<
      number[]
   >([])
   const [productPayrollStateCounts, setProductPayrollStateCounts] = useState<
      number[]
   >([])
   const [productPayrollEINImpTypes, setProductPayrollEINImpTypes] = useState<
      string[]
   >([])
   const [productEINIntegrations, setProductEINIntegrations] = useState<
      ProductEINIntegration[][]
   >([])
   const [einIntegrationsCount, setEinIntegrationsCount] = useState(0)

   const onProductPayrollEINCountChange = (newEINCount: number) => {
      let prevEINCount = productPayrollEINCount

      // Limits the number of EINs to 80
      if (newEINCount > 80) newEINCount = 80

      // Prevent the count from going negative
      if (newEINCount < 0) {
         onProductPayrollEINCountChange(0)
         return
      }
      if (newEINCount === 0) {
         setPayrollImpType('none')
      }
      if (prevEINCount === 0 && newEINCount > 0) {
         setPayrollImpType('full')
      }

      const sfEINCount = accountSnowflake?.EINs__c?.[0]?.length ?? 0
      const hadNewEINs = differenceQuote
         ? prevEINCount > sfEINCount
         : prevEINCount > 0
      const hasNewEINs = differenceQuote
         ? newEINCount > sfEINCount
         : newEINCount > 0
      if (!hadNewEINs && hasNewEINs) {
         if (
            payrollFirstPayDate === null &&
            hasPermission('PayrollFirstPayDate', user_role)
         ) {
            setPayrollFirstPayDate(activationDate)
         }
      } else if (hadNewEINs && !hasNewEINs) {
         setPayrollFirstPayDate(null)
      }

      // If adding EINs or no data is present, proceed without confirmation
      if (Number(newEINCount) < Number(prevEINCount)) {
         let newEECounts = productPayrollEECounts.slice(0, newEINCount)
         let newStateCounts = productPayrollStateCounts.slice(0, newEINCount)

         // Update the total EE count and State count
         let newEECount = newEECounts.reduce((total, a) => total + a, 0)
         let newStateCount = newStateCounts.reduce(
            (total, a) => (a > 0 ? total + a - 1 : total + a),
            0,
         )
         setProductPayrollEECount(newEECount)
         setProductPayrollTotalStateCount(newStateCount)
         // If adding EINs, add empty data to the arrays
      } else if (Number(newEINCount) > Number(prevEINCount)) {
         let newEINNames, newEECounts, newStateCounts
         // If the new count is greater than the current arrays, add empty data to the arrays
         if (
            newEINCount > productPayrollEINNames.length &&
            newEINCount > productPayrollEECounts.length &&
            newEINCount > productPayrollStateCounts.length
         ) {
            newEINNames = productPayrollEINNames.concat(
               Array(newEINCount - prevEINCount).fill(''),
            )
            newEECounts = productPayrollEECounts.concat(
               Array(newEINCount - prevEINCount).fill(0),
            )
            newStateCounts = productPayrollStateCounts.concat(
               Array(newEINCount - prevEINCount).fill(0),
            )

            setProductPayrollEINNames(newEINNames)
            setProductPayrollEECounts(newEECounts)
            setProductPayrollStateCounts(newStateCounts)
            // Updates the total EE count and State count to reflect the existing data
         } else {
            // Splite arrays from 0 to newEINCount
            newEECounts = productPayrollEECounts.slice(0, newEINCount)
            newStateCounts = productPayrollStateCounts.slice(0, newEINCount)

            let newEECount = newEECounts.reduce((total, a) => total + a, 0)
            let newStateCount = newStateCounts.reduce(
               (total, a) => (a > 0 ? total + a - 1 : total + a),
               0,
            )
            setProductPayrollEECount(newEECount)
            setProductPayrollTotalStateCount(newStateCount)
         }
      }
      setProductPayrollEINCount(newEINCount)
   }

   // total of all payroll ee counts
   const [productPayrollTotalEECount, setProductPayrollEECount] = useState(0)

   const onProductPayrollEINEECountChange = (
      newEECount: number,
      index: number,
   ) => {
      if (newEECount < 0) return 0

      let totalPrevEECount = productPayrollTotalEECount
      let EINCount = productPayrollEINCount
      let StateCount = productPayrollStateCounts[index]
         ? productPayrollStateCounts[index]
         : 1

      const newEECounts = [...productPayrollEECounts]
      const newStateCounts = [...productPayrollStateCounts]
      newEECounts[index] = newEECount
      newStateCounts[index] = StateCount
      setProductPayrollEECounts(newEECounts)
      setProductPayrollStateCounts(newStateCounts)

      let totalNewEECount = 0
      let totalNewStateCount = 0
      if (EINCount > 0) {
         totalNewEECount = newEECounts
            .slice(0, EINCount)
            .reduce((partialSum, a) => partialSum + a, 0)
         totalNewStateCount = newStateCounts
            .slice(0, EINCount)
            .reduce((total, a) => (a > 0 ? total + a - 1 : total + a), 0)
      }
      setProductPayrollTotalStateCount(totalNewStateCount)
      setProductPayrollEECount(totalNewEECount)

      // Updates discounts that depend on the product count
      onMainProductChange('payroll', totalNewEECount)
      if (totalPrevEECount < 1 && totalNewEECount > 0) {
         setPayrollImpType('full')
      } else if (totalPrevEECount > 0 && totalNewEECount < 1) {
         setPayrollImpType('none')
         removeAllPromoDiscountForProduct(
            'payrollImp',
            discount_list,
            promotionalDiscounts,
            setPromotionalDiscounts,
         )
      }
   }

   const onProductPayrollEINEECountInputBlur = (
      EECount: number,
      index: number,
   ) => {
      let totalPrevEECount = productPayrollTotalEECount
      let EINCount = productPayrollEINCount
      let StateCount = productPayrollStateCounts[index]
      if (EECount < 1 || isNaN(EECount)) {
         EECount = 0
      }
      if (EECount === 0) {
         onProductPayrollEINEECountChange(EECount, index)
         onProductPayrollEINStateCountChange(0, index)
      }
      if (EECount > 0 && StateCount < 1) {
         onProductPayrollEINStateCountChange(1, index)
      }
   }

   const onProductPayrollEINStateCountChange = (
      newStateCount: number,
      index: number,
   ) => {
      let EINCount = productPayrollEINCount
      if (EINCount > 0) {
         const newStateCounts = [...productPayrollStateCounts]
         newStateCounts[index] = newStateCount
         setProductPayrollStateCounts(newStateCounts)
         let totalNewStateCount = newStateCounts
            .slice(0, EINCount)
            .reduce((total, a) => (a > 0 ? total + a - 1 : total + a), 0)
         setProductPayrollTotalStateCount(totalNewStateCount)
      }
   }

   const onProductPayrollImpTypeChange = (
      newImpType: string,
      index: number,
   ) => {
      let EINCount = productPayrollEINCount
      if (EINCount > 0) {
         const newImpTypes = [...productPayrollEINImpTypes]
         newImpTypes[index] = newImpType
         setProductPayrollEINImpTypes(newImpTypes)
         // Match first EIN in EIN table
         if (index == 0) {
            setPayrollImpType(newImpType)
         }
         // This is for use in calculate.mjs later. The "higher" imp type is always taken.
         // If newImpTypes (any of the EINs imp types) contains "full" set the quote' general payrollImpType to "full".
         // Else if it contains "foundations" set the quote's general payrollImpType to "foundations"
         // Otherwise set it to "none"
         if (newImpTypes.includes('full')) {
            setPayrollImpType('full')
         } else if (newImpTypes.includes('foundations')) {
            setPayrollImpType('foundations')
         } else {
            setPayrollImpType('none')
         }
      }
   }
   const onProductPayrollEECountChange = (newEECount: number) => {
      let prevEECount = productPayrollTotalEECount
      setProductPayrollEECount(newEECount)
      //NOTE: Should this have an additional condition of !(user_role == "Expansion" || user_role == "Partner Sales" || user_role == "Admin")
      if (prevEECount < 1 && newEECount > 0) {
         setPayrollImpType('full')
      } else if (prevEECount > 0 && newEECount < 1) {
         setPayrollImpType('none')
         removeAllPromoDiscountForProduct(
            'payrollImp',
            discount_list,
            promotionalDiscounts,
            setPromotionalDiscounts,
         )
      }
   }
   const onProductPayrollEINNameChange = (newName: string, index: number) => {
      const updatedEINNames = [...productPayrollEINNames]
      updatedEINNames[index] = newName
      setProductPayrollEINNames(updatedEINNames)
   }

   /**
    * Adds new empty integration to the list of integrations for the given EIN
    *
    * @param {Integer} einIndex
    */
   const onProductEINIntegrationAdd = (einIndex: number) => {
      // Gets the current integrations for given EIN
      const updatedIntegrations: ProductEINIntegration[] =
         productEINIntegrations[einIndex] || []
      const emptyIntegration: ProductEINIntegration = {
         integrationType: 'None',
         solutionCode: 'None',
         solutionCost: 0,
      }
      // Push an empty integration to the list
      updatedIntegrations.push(emptyIntegration)

      // Updates the integrations for the given EIN
      const newIntegrations: ProductEINIntegration[][] = [
         ...productEINIntegrations,
      ]
      newIntegrations[einIndex] = updatedIntegrations
      setProductEINIntegrations(newIntegrations)
      setEinIntegrationsCount(
         newIntegrations.reduce((total, ein) => total + ein.length, 0),
      )
   }
   /**
    * Removes the integration at the given index for the given EIN
    *
    * @param {Integer} einIndex
    * @param {Integer} integrationIndex
    */
   const onProductEINIntegrationRemove = (
      einIndex: number,
      integrationIndex: number,
   ) => {
      // Gets the current integrations for given EIN
      const updatedIntegrations: ProductEINIntegration[] =
         productEINIntegrations[einIndex] || []
      // Removes the integration at the given index
      updatedIntegrations.splice(integrationIndex, 1)

      // Updates the integrations for the given EIN
      const newIntegrations: ProductEINIntegration[][] = [
         ...productEINIntegrations,
      ]
      newIntegrations[einIndex] = updatedIntegrations
      setProductEINIntegrations(newIntegrations)
      setEinIntegrationsCount(
         newIntegrations.reduce((total, ein) => total + ein.length, 0),
      )
   }
   /**
    * Updates the integration type for the given EIN and integration index. It replaces the current value with the given one
    * @param {Integer} einIndex
    * @param {Integer} integrationIndex
    * @param {object} value
    */
   const onIntegrationChange = (
      einIndex: number,
      integrationIndex: number,
      value: ProductEINIntegration,
   ) => {
      // Gets the current integrations for given EIN
      const updatedIntegrations: ProductEINIntegration[] =
         productEINIntegrations[einIndex] || []
      // Updates the integration type for the given index
      updatedIntegrations[integrationIndex] = value

      // Updates the integrations for the given EIN
      const newIntegrations: ProductEINIntegration[][] = [
         ...productEINIntegrations,
      ]
      newIntegrations[einIndex] = updatedIntegrations
      setProductEINIntegrations(newIntegrations)
   }

   // total of all payroll states counts
   const [productPayrollTotalStateCount, setProductPayrollTotalStateCount] =
      useState(0)

   // state for imp type radio button
   const [impType, setImpType] = useState('selfSetup')
   // state for implementation type
   const impTypes = [
      { value: 'selfSetup', name: 'None' },
      { value: 'quickstart', name: 'QuickStart' },
      { value: 'full', name: 'Full' },
      { value: 'premier', name: 'Premier' },
   ]

   // function to handle change of core product
   const onImpOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const oldImpType: string = impType
      const newImpType: string = e.target.value
      if (oldImpType === newImpType) return
      else {
         if (newImpType !== 'quickstart') {
            setDiscountQuickstartTraining(false)
         }
         if (newImpType !== 'full' && newImpType !== 'premier') {
            setDiscountImpPromotion(false)
            setDiscountDealDeskHRImp(false)
         }
      }
      setImpType(e.target.value)
   }

   // state for imp type radio button
   const [benAdminImpType, setBenAdminImpType] = useState('none')
   // state for implementation type
   const benAdminImpTypes = [
      { value: 'none', name: 'None' },
      { value: 'full', name: 'Full' },
   ]
   // function to handle change of core product
   const onBenAdminImpOptionChange = (
      e: React.ChangeEvent<HTMLInputElement>,
   ) => {
      setBenAdminImpType(e.target.value)
   }

   // state for imp type radio button
   const [payrollImpType, setPayrollImpType] = useState('none')
   // state for implementation type
   const payrollImpTypes = [
      { value: 'none', name: 'None' },
      { value: 'foundations', name: 'Foundations' },
      { value: 'full', name: 'Full' },
   ]
   // function to handle change of core product
   const onPayrollImpOptionChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      value = null,
      index = null,
   ) => {
      const newImpType = e.target.value
      setPayrollImpType(newImpType)
      if (newImpType === 'none')
         removeAllPromoDiscountForProduct(
            'payrollImp',
            discount_list,
            promotionalDiscounts,
            setPromotionalDiscounts,
         )

      // Always update the implementation type for the first EIN (index 0)
      setProductPayrollEINImpTypes((prev) => {
         const updated = [...prev]
         updated[0] = newImpType
         return updated
      })

      if (value != null && index != null) {
         onProductPayrollImpTypeChange(value, index)
      }
   }

   // state for quote type radio buttons
   const [quoteType, setQuoteType] = useState('full')
   const quoteTypes = [
      { value: 'full', name: 'Full' },
      { value: 'monthly', name: 'Monthly' },
      { value: 'onetime', name: 'Onetime' },
   ]

   // state for prepayment type (whether the prepayment should use Diff for full term or only the first month)
   const [
      prepaymentActivationUseDifference,
      setPrepaymentActivationUseDifference,
   ] = useState(false)
   // state for all dynamic products
   const [dynamicProducts, setDynamicProducts] = useState(new Map())

   // function to set a specific dynamic product
   //@ts-ignore
   const setDynamicProduct = (productCode: string, value: any) => {
      setDynamicProducts((prev) => {
         const newMap = new Map(prev)
         newMap.set(productCode, value)
         return newMap
      })
   }

   // function to handle the clear/reset button
   //TODO: make this loop through products in product_list for current pricebook, and set them all to a default value from the product_list
   const onClear = (e: React.MouseEvent<HTMLButtonElement>) => {
      setCoreProductSelected('core2')
      setProductCoreEECount(0)
      setImpType('selfSetup')
      setBenAdminImpType('none')
      setPayrollImpType('none')
      setProductPerformanceEECount(0)
      setProductTimeTrackingEECount(0)
      setHasUSEmployees(false)
      setIsATSOnly(false)
      setProductBenefitsAdministrationEECount(0)
      setProductPayrollEECount(0)
      setProductPayrollTotalStateCount(0)
      setDiscountBundle(false)
      setDiscountNonProfit(false)
      setDiscountPrepaymentYears('0')
      setDiscountMultiEntity(false)
      setDiscountHRMonthlyPromotion(false)
      setDiscountHRDealDeskMonthly(false)
      setDiscountReseller(0)
      setDiscountQuickstartTraining(false)
      setDiscountImpPromotion(false)
      setDiscountDealDeskHRImp(false)
      setDiscountDealDeskPayrollImp(false)
      setDiscountSecondEIN(false)
      setDiscountGroupImp(false)
      setProductPayrollEINCount(0)
      setProductPayrollEINNames([])
      setProductPayrollEECounts([])
      setProductPayrollStateCounts([])
      setProductPayrollEINImpTypes([])
      setPayrollFirstPayDate(null)
      setDiscountDealDeskHRImpRate(0)
      setDiscountDealDeskQuickstartImpRate(0)
      setDiscountDealDeskPayrollImpRate(0)
      setProductTestAccount(false)
      setProductAdditionalJobsCount(0)
      setProductAdditionalStorageCount(0)
      setProductHistoricalDataImport(false)
      setMarketplaceListing(false)
      setProductSponsorship(false)
      setProductPaidServiceAmount(0)
      setQuoteType('full')
      setDifferenceQuote(false)
      setPromotionalDiscounts({})
      setMonthlyTax(0)
      setAccountBalance(0)
      setProductTimeClockDetails([])
      setProductHasTimeClocks(false)
      setProductEINIntegrations([])
      setProfessionalServiceDetails([])
      setConsultingServiceDetails([])
      setPrepaymentActivationUseDifference(false)
      setDynamicProducts(new Map())
   }

   // state to show or hide us only products
   const [hasUSEmployees, setHasUSEmployees] = useState(false)
   // state for ATS-only opportunities
   const [isATSOnly, setIsATSOnly] = useState(false)
   // state for quote expiration date
   const [quoteExpirationDate, setQuoteExpirationDate] = useState<
      string | null
   >(null)
   // state for optional closed stage (close won, or close lost)
   const [isClosed, setIsClosed] = useState(false)

   const onExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuoteExpirationDate(e.target.value)
   }

   // state for quote activation date.
   const [activationDate, setActivationDate] = useState<string | null>(null)
   const [nextInvoiceDate, setNextInvoiceDate] = useState<string | null>(null)

   // state for payroll first pay date
   const [payrollFirstPayDate, setPayrollFirstPayDate] = useState<
      string | null
   >(null)

   const onPayrollFirstPayDateChange = (
      e: React.ChangeEvent<HTMLInputElement>,
   ) => {
      setPayrollFirstPayDate(e.target.value || null)
   }

   const onProductAdditionalJobsCountChange = (newCount: number) => {
      // Updates discounts that depend on the product count
      onMainProductChange('addJobPosting', newCount)
      setProductAdditionalJobsCount(newCount)
   }

   const onProductAdditionalStorageCountChange = (newCount: number) => {
      // Updates discounts that depend on the product count
      onMainProductChange('addStorage', newCount)
      setProductAdditionalStorageCount(newCount)
   }

   const onProductPaidServiceAmountChange = (newAmount: number) => {
      // Updates discounts that depend on the product count
      onMainProductChange('paidService', newAmount)
      setProductPaidServiceAmount(newAmount)
   }

   const onProductPerformanceEECountChange = (newCount: number) => {
      // Updates discounts that depend on the product count
      onMainProductChange('performanceMan', newCount)
      setProductPerformanceEECount(newCount)
   }

   const onProductTimeTrackingEECountChange = (newCount: number) => {
      // Updates discounts that depend on the product count
      onMainProductChange('timeTracking', newCount)
      setProductTimeTrackingEECount(newCount)
   }

   /**
    * Function to handle the change of the product count for the main product in case there are discounts that depend on it.
    * This function will remove any discounts that depend on the product count if the new count is not between
    * the EmpLow and EmpHigh values of the discount.
    *
    * @param {*} productCode Product Code of the main product
    * @param {*} newProductCount New product count for the main product
    */
   const onMainProductChange = (
      productCode: string,
      newProductCount: number,
   ) => {
      // Find all discounts where productCode is in the dependsOn field
      const discountKeys = Object.values(discount_list).filter(
         (discount) => discount.DependsOn === productCode,
      )
      // iterates through all discounts that depend on the productCode
      discountKeys.forEach((discount) => {
         // Check if discount.DiscountCode is in promotionalDiscounts
         if (discount.DiscountCode in promotionalDiscounts) {
            // Check if newProductCount is not between discount.EmpLow and discount.EmpHigh
            if (
               newProductCount < discount.EmpLow ||
               newProductCount > discount.EmpHigh
            ) {
               // Remove discount from promotionalDiscounts
               setPromotionalDiscounts((prev) => {
                  delete prev[discount.DiscountCode]
                  return { ...prev }
               })
            }
         }
      })
   }

   ///////////////////////////////
   // Define Discount Input States
   ///////////////////////////////
   // state for quickstart discount
   const [discountQuickstartTraining, setDiscountQuickstartTraining] =
      useState<boolean>(false)
   // state for bundle discount
   const [discountBundle, setDiscountBundle] = useState<boolean>(false)
   // state for non profit
   const [discountNonProfit, setDiscountNonProfit] = useState<boolean>(false)
   // state for payment term years discount
   const [discountPrepaymentYears, setDiscountPrepaymentYears] =
      useState<string>('0')
   // pre payment data terms in years
   const [discountPrepaymentData, setDiscountPrepaymentData] = useState<
      SimpleOption[]
   >([])
   const [discountPrepaymentDataSaved, setDiscountPrepaymentDataSaved] =
      useState<SimpleOption[]>([])

   // state for hr monthly promotion discount
   const [discountHRMonthlyPromotion, setDiscountHRMonthlyPromotion] =
      useState<boolean>(false)
   // state for deal desk discount
   const [discountHRDealDeskMonthly, setDiscountHRDealDeskMonthly] =
      useState<boolean>(false)
   // state for reseller discount
   const [discountReseller, setDiscountReseller] = useState<number>(0)
   // state for related entity discount
   const [discountMultiEntity, setDiscountMultiEntity] =
      useState<boolean>(false)
   // state for second ein discount
   const [discountSecondEIN, setDiscountSecondEIN] = useState<boolean>(false)

   const [discountImpPromotion, setDiscountImpPromotion] =
      useState<boolean>(false)
   const [discountDealDeskHRImp, setDiscountDealDeskHRImp] =
      useState<boolean>(false)
   const [discountDealDeskPayrollImp, setDiscountDealDeskPayrollImp] =
      useState<boolean>(false)
   const [discountGroupImp, setDiscountGroupImp] = useState<boolean>(false)

   const [discountDealDeskHRImpRate, setDiscountDealDeskHRImpRate] =
      useState<number>(0)
   const [
      discountDealDeskQuickstartImpRate,
      setDiscountDealDeskQuickstartImpRate,
   ] = useState<number>(0)
   const [discountDealDeskPayrollImpRate, setDiscountDealDeskPayrollImpRate] =
      useState<number>(0)

   //lock set for Dealdesk
   const [dealDeskLocks, setDealDeskLocks] = useState(new Set<string>())

   function addDealDeskLock(lock: string) {
      setDealDeskLocks((prevLocks) => new Set(prevLocks).add(lock))
   }
   function removeDealDeskLock(lock: string) {
      setDealDeskLocks((prevLocks) => {
         const newLocks = new Set(prevLocks)
         newLocks.delete(lock)
         return newLocks
      })
   }
   function clearDealDeskLocks() {
      setDealDeskLocks(new Set())
   }
   //discounts to ignore for deal desk
   const [
      ignoreHRISImplementationDealDeskDiscountList,
      setIgnoreHRISImplementationDealDeskDiscountList,
   ] = useState(new Set(['hrDealdesk']))
   const [
      ignorePayrollImplementationDealDeskDiscountList,
      setIgnorePayrollImplementationDealDeskDiscountList,
   ] = useState(new Set(['payDealdesk', 'secondEINDiscount']))
   const [
      ignoreQuickstartDealDeskDiscountList,
      setIgnoreQuickstartDealDeskDiscountList,
   ] = useState(new Set(['quickstartDiscount']))

   // state for expansion products
   const [productAdditionalStorageCount, setProductAdditionalStorageCount] =
      useState(0)
   const [productAdditionalJobsCount, setProductAdditionalJobsCount] =
      useState(0)
   const [productHistoricalDataImport, setProductHistoricalDataImport] =
      useState(false)
   const [productMarketplaceListing, setMarketplaceListing] = useState(false)
   const [productSponsorship, setProductSponsorship] = useState(false)
   const [productPaidServiceAmount, setProductPaidServiceAmount] = useState(0)
   const [productTestAccount, setProductTestAccount] = useState(false)

   const [quoteNotes, setQuoteNotes] = useState('')
   const [prevPricebookId, setPrevPricebookId] = useState<number | null>(null)
   const [prevProductList, setPrevProductList] = useState<ProductState | null>(
      null,
   )
   const [prevCoreEECount, setPrevCoreEECount] = useState<number>(0)

   // Fetch previous pricebook products data using hook
   const { data: prevProductsData, isLoading: isLoadingPrevProducts } =
      useGetPrevProducts(
         prevPricebookId ? String(prevPricebookId) : '',
         currencyCode,
      )

   const [differenceQuote, setDifferenceQuote] = useState(false)
   const [accountSnowflake, setAccountSnowflake] =
      useState<AccountSnowflakeData>({})
   const [accountSnowflakeLoaded, setAccountSnowflakeLoaded] = useState(false)

   const onActivationDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newDate = e.target.value
      setActivationDate(newDate)
      const isFrozenOnADChange = isFrozenStage(stage)
      if (!isFrozenOnADChange) {
         setNextInvoiceDate(
            resolveNextInvoiceDate({
               isFrozen: false,
               savedDbNid: null,
               snowflakeNid: accountSnowflake?.Next_Invoice_Date__c,
               activationDate: newDate,
               currentDate: timeData?.currentDate,
            }),
         )
      }
      const hasNewEINs = differenceQuote
         ? productPayrollEINCount >
           (accountSnowflake?.EINs__c?.[0]?.length ?? 0)
         : productPayrollEINCount > 0
      if (payrollFirstPayDate && newDate && payrollFirstPayDate < newDate) {
         setPayrollFirstPayDate(newDate)
      } else if (
         !payrollFirstPayDate &&
         newDate &&
         hasNewEINs &&
         hasPermission('PayrollFirstPayDate', user_role)
      ) {
         setPayrollFirstPayDate(newDate)
      }
   }
   const [coreCountryCount, setCoreCountryCount] = useState(0)

   // Fetch pricing adjustments using hook (React Query handles caching and stale request cancellation)
   // Only fetch after accountSnowflake has been loaded (even if empty) to avoid race conditions
   const { data: pricingAdjustmentsData } = useGetPricingAdjustments(
      pricebookId ? String(pricebookId) : '',
      currencyCode,
      accountSnowflake,
      accountSnowflakeLoaded,
   )

   // Tracks discount codes injected by the last pricing adjustments
   // fetch so they can be cleared before re-applying on pricebook switch.
   const pricingAdjDiscountKeysRef = useRef<string[]>([])

   // Tracks DiscountFamily names that the user has explicitly set (loaded from DB
   // or manually chosen in the UI). Pricing adjustments must never overwrite these.
   const userSetFamilyKeysRef = useRef<Set<string>>(new Set())

   // Call whenever promotional discounts arrive from the DB or user interaction
   // to mark any contained family keys as user-owned. Also scrubs the pricing
   // adjustment cleanup ref so a previous fetch's keys can't delete the user's
   // selection when the effect re-fires.
   const recordUserSetFamilyKeys = useCallback(
      (discounts: Record<string, unknown>) => {
         const incoming = extractFamilyKeys(discounts)
         const newlyOwned: string[] = []
         for (const key of incoming) {
            if (!userSetFamilyKeysRef.current.has(key)) newlyOwned.push(key)
            userSetFamilyKeysRef.current.add(key)
         }
         if (newlyOwned.length === 0) return
         pricingAdjDiscountKeysRef.current =
            pricingAdjDiscountKeysRef.current.filter((k) => {
               if (newlyOwned.includes(k)) return false
               const family = discount_list[k]?.DiscountFamily
               return !family || !userSetFamilyKeysRef.current.has(family)
            })
      },
      [discount_list],
   )

   const [PromoUTM, setPromoUTM] = useState<string | null>('')
   const [accountFlags, setAccountFlags] = useState<string | null>('')
   const [promotionalDiscounts, setPromotionalDiscounts] =
      useState<PromotionalDiscounts>({})

   // Prepayment and Term Contracts
   const [prepaymentDiscounts, setPrepaymentDiscounts] = useState({}) // State for discount that requires prepayment
   const [termLength, setTermLength] = useState<number>(1) // Set default to month-to-month
   const [prepayLength, setPrepayLength] = useState<number>(1) // NEW: Prepay length in months (decoupled from term)
   const [isCoTerm, setIsCoTerm] = useState<boolean>(false) // Whether the selected term is a co-term option

   const [accountBalance, setAccountBalance] = useState<number>(0)
   const [monthlyTax, setMonthlyTax] = useState<number>(0)

   const [professionalServiceDetails, setProfessionalServiceDetails] = useState<
      ServiceDetails[]
   >([])
   const [consultingServiceDetails, setConsultingServiceDetails] = useState<
      ServiceDetails[]
   >([])

   // Opportunity type can either be 'New' or 'Expansion'. If neither, it will default to 'New'.
   const [opportunityType, setOpportunityType] = useState<string>('New')

   const [accountStatus, setAccountStatus] = useState<string>('')

   // This is set from the RecordTypeId field attached to an SF Opportunity. Only used for creating Opportunity Products and logic in that process, not in other calculations or ones that a client would see.
   const [recordType, setRecordType] = useState<string>('')

   const [partnerAccount, setPartnerAccount] = useState<string | null>('')

   const [region, setRegion] = useState<string | null>('')

   // Term Lengths Hook
   const { data: termLengthsList, isLoading: isLoadingTermLengths } =
      useTermLengths(opportunity_id, user_role)

   // Prepayment Lengths Hook
   const { data: prepayLengthsList, isLoading: isLoadingPrepayLengths } =
      usePrepayLengths(opportunity_id, user_role)

   // Fetch discounts data using hook
   const { data: discountsData, isLoading: isLoadingDiscounts } =
      useGetDiscounts(
         pricebookId ? String(pricebookId) : '',
         currencyCode,
         PromoUTM || '',
         accountFlags || '',
         opportunityType,
         JSON.stringify(accountSnowflake),
         partnerAccount || '',
         region || '',
         opportunity_id,
         user_role,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         discountReseller,
      )

   // Payroll Info Records fetched from backend (if exists)
   const [payrollInfoRecords, setPayrollInfoRecords] = useState<PayrollInfo[]>(
      [],
   )

   // Deal Desk Discount Records fetched from backend (if exists)
   const [dealDeskDiscountRecords, setDealDeskDiscountRecords] = useState<
      DealDeskDiscount[]
   >([])

   const EOR_RECORD_TYPE_ID = '012KZ000000kf7tYAA'

   /////////////////////////////////////////////////
   // Fetch Query Parameters
   /////////////////////////////////////////////////

   // get query parameters from url
   useEffect(() => {
      const urlSearchString = window.location.search
      const params = new URLSearchParams(urlSearchString)
      // set variables from query parameters
      setopportunity_id(params.get('opportunity_id') || '')
      setparent_opportunity_id(params.get('parent_opportunity_id') || '')
      setaccount_name(params.get('account_name') || '')
      setopportunity_name(params.get('opportunity_name') || '')
      setopportunity_contact_name(params.get('opportunity_contact_name') || '')
      setopportunity_contact_email(
         params.get('opportunity_contact_email') || '',
      )
      setlast_agent_email(params.get('last_agent_email') || '')
      setlast_agent_name(params.get('last_agent_name') || '')
      setAccountStatus(params.get('account_status') || '')
      setRecordType(params.get('record_type') || '')
      setPartnerAccount(params.get('partner_account') || '')
      setRegion(params.get('region') || '')
      setCompanyId(params.get('company_id') || '')
      if (params.get('Reseller_Disc') != null)
         setDiscountReseller(Number(params.get('Reseller_Disc')))
      let stage = params.get('stage')
      if (stage != null) {
         stage = stage.toLowerCase()
         if (stage == 'closed lost' || stage == 'closed won') {
            setIsClosed(true)
            toast.error('This opportunity is closed. All input is disabled.', {
               duration: 6000,
            })
         }
      }
   }, [])

   ////////////////////////////////////////////////
   // Set Integration Types And Solutions
   ////////////////////////////////////////////////
   useEffect(() => {
      if (integrationObjects) {
         setIntegrationTypes(integrationObjects?.integrationTypes || [])
         setIntegrationSolutions(integrationObjects?.integrationSolutions || [])
      }
   }, [integrationObjects])

   /////////////////////////////////////////////////
   // Fetch Time, Product, Discount, Opp Data from Backend
   /////////////////////////////////////////////////

   // Get current time from time api, sets all time based variables
   useEffect(() => {
      if (timeData) {
         // Set quote expiration date as 14 days from now
         let currentDate = new Date(timeData.currentDate)
         currentDate.setDate(currentDate.getDate() + 14)
         setQuoteExpirationDate(currentDate.toISOString().split('T')[0])
      }
   }, [timeData])

   // lookup pricebooks using hook data
   useEffect(() => {
      if (pricebooksData && !isLoadingPricebooks) {
         const data = pricebooksData
         // Sort pricebooks deterministically: prevPricebookId first, then by ID ascending
         let sortedPricebookList = Array.isArray(data)
            ? [...data].sort((a, b) => {
                 // prevPricebookId always comes first
                 if (a.value == prevPricebookId && b.value != prevPricebookId)
                    return -1
                 if (a.value != prevPricebookId && b.value == prevPricebookId)
                    return 1
                 // Otherwise sort by pricebook value (ID) ascending for consistent ordering
                 return Number(a.value) - Number(b.value)
              })
            : [{}]
         // set state when the data received (use sorted list for consistent ordering)
         setPricebooks(sortedPricebookList)
         setPricebookSelectorList(
            sortedPricebookList.map((e, key) => {
               // Currency filtering logic:
               // If previousCurrencyCode is USD: allow all options
               // If previousCurrencyCode is NOT USD: only allow USD pricebooks or same currency pricebooks
               // This prevents invalid currency conversions like CAD -> GBP
               const isEnabled =
                  previousCurrencyCode === 'USD' ||
                  e.currencyCode === 'USD' ||
                  e.currencyCode === previousCurrencyCode

               return (
                  <option key={key} value={e.value} disabled={!isEnabled}>
                     {e.name}
                  </option>
               )
            }),
         )

         if (sortedPricebookList.length > 0) {
            setPricebookId(sortedPricebookList[0]['value'])
         } else {
            setPricebookId(null)
         }
      }
   }, [
      pricebooksData,
      isLoadingPricebooks,
      prevPricebookId,
      previousCurrencyCode,
   ])

   // Apply pricing adjustments from the React Query hook data into promotionalDiscounts.
   //
   // Rule: pricing adjustments only fill in families the user has NOT set. If the DB
   // (or a manual selection) provided a value for a DiscountFamily, that family is
   // off-limits — we don't touch its family key, its boolean flag, or its number value.
   useEffect(() => {
      if (!pricingAdjustmentsData) return

      let newPricingAdjKeys: string[] = []
      setPromotionalDiscounts((prev) => {
         const { newState, newPricingAdjKeys: written } =
            mergePricingAdjustments({
               prevState: prev as Record<string, unknown>,
               pricingData: pricingAdjustmentsData,
               userOwnedFamilies: userSetFamilyKeysRef.current,
               prevPricingAdjKeys: pricingAdjDiscountKeysRef.current,
               discountList: discount_list,
            })
         newPricingAdjKeys = written
         return newState as PromotionalDiscounts
      })
      pricingAdjDiscountKeysRef.current = newPricingAdjKeys
   }, [pricingAdjustmentsData])

   // lookup products and set product_list using hook data
   useEffect(() => {
      if (productsData && !isLoadingProducts && pricebookId !== null) {
         setproduct_list(productsData)
      }
   }, [productsData, isLoadingProducts, pricebookId])

   // lookup previous pricebook products and set prevProductList using hook data
   useEffect(() => {
      if (
         prevProductsData &&
         !isLoadingPrevProducts &&
         prevPricebookId !== null
      ) {
         setPrevProductList(prevProductsData)
      }
   }, [prevProductsData, isLoadingPrevProducts, prevPricebookId])

   // lookup pricebook and set discount_list using hook data
   useEffect(() => {
      if (
         discountsData &&
         !isLoadingDiscounts &&
         (!opportunity_id ||
            (dealDeskDiscountsData && !isLoadingDealDeskDiscounts)) &&
         pricebookId !== null
      ) {
         // Set prepayment discount records
         setPrepaymentDiscounts(getPrepaymentDiscounts(discountsData))
         // Set deal desk discount records from backend
         const backendDealDeskDiscounts = dealDeskDiscountsData?.discounts || []
         setDealDeskDiscountRecords(backendDealDeskDiscounts)

         // Get the set of deal desk discount IDs that should exist
         const validDealDeskIds = new Set(
            // @ts-ignore
            backendDealDeskDiscounts.map((d) => d.id),
         )

         // Filter out deal desk discounts from backend that are no longer in dealDeskDiscountRecords
         // (i.e., they were removed in the modal but not yet saved to backend)
         const filteredData: Record<string, Discount> = {}
         Object.entries(discountsData).forEach(([key, discount]) => {
            // Check if this is a deal desk discount (UUID format)
            const isUUID =
               /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                  key,
               )
            const isDealDeskDiscount = isUUID

            if (isDealDeskDiscount) {
               // Only include if it's still in dealDeskDiscountRecords
               if (validDealDeskIds.has(key)) {
                  filteredData[key] = discount as Discount
               }
            } else {
               // Not a deal desk discount, always include
               filteredData[key] = discount as Discount
            }
         })

         // Convert NEW (unsaved) deal desk discounts and merge with filtered data
         // This includes both temp-* IDs (newly created) and UUID IDs (not yet in backend response)
         const newDealDeskDiscounts: Record<string, Discount> = {}

         // @ts-ignore
         backendDealDeskDiscounts.forEach((ddDiscount) => {
            // Process if it's a temp ID or a UUID that's not already in filtered data
            const isTempId = ddDiscount.id.startsWith('temp-')
            const isUUID =
               /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                  ddDiscount.id,
               )

            if (isTempId || (isUUID && !filteredData[ddDiscount.id])) {
               const converted = convertDealDeskDiscountToStandardFormat(
                  ddDiscount,
                  product_list,
               )
               newDealDeskDiscounts[converted.DiscountCode] = converted
            }
         })

         // Merge filtered discounts with new deal desk discounts
         const merged = {
            ...filteredData,
            ...newDealDeskDiscounts,
         }
         setdiscount_list(merged)

         // Note: Deal desk discount IDs are automatically added to promotional_discounts
         // by the server when loading the opportunity (in data.js getOpportunity)
         // New discounts created in the modal use temp IDs which are added by DealDeskDiscountCreatorButton
      }
   }, [
      discountsData,
      isLoadingDiscounts,
      pricebookId,
      dealDeskDiscountsData,
      isLoadingDealDeskDiscounts,
      opportunity_id,
      product_list,
   ])

   /**
    * Returns a list of discounts that have a prepayment discount minimun requirement.
    *
    * @param {Object} discounts list of discounts
    * @returns {Object} prepaymentDiscounts list of discounts with prepayment discount minimum requirement
    */
   function getPrepaymentDiscounts(discounts: DiscountState) {
      const prepaymentDiscounts: Record<string, Discount> = {}
      for (const key in discounts) {
         if (
            discounts.hasOwnProperty(key) &&
            typeof discounts[key].MinRequiredPrepayment === 'string'
         ) {
            prepaymentDiscounts[key] = discounts[key]
         }
      }
      return prepaymentDiscounts
   }

   ////////////////////////////////////////////////////////////
   // Set Discount Prepayment Data based on Feature Permissions
   ////////////////////////////////////////////////////////////
   useEffect(() => {
      const fetchData = async () => {
         try {
            // Fetches the permissions since this useEffect before the permissions are set.
            const extendedLabelRoles =
               featurePermissions?.DiscountPrepaymentExtendedLabels ?? []
            let prepaymentOptions =
               getDiscountPrepaymentData(extendedLabelRoles)
            setDiscountPrepaymentData(prepaymentOptions)
            setDiscountPrepaymentDataSaved(prepaymentOptions)
         } catch (error) {
            console.error('Error fetching data:', error)
         }
      }
      fetchData()
   }, [user_role, isLoadingPermissions])

   /**
    * Function to get the discount prepayment data based on the user's role.
    *
    * @param {*} permissions List of permissions for the user
    * @returns {SimpleOption[]} List of discount prepayment data based on the user's role
    */
   function getDiscountPrepaymentData(permissions: string[]): SimpleOption[] {
      if (permissions && permissions.includes(user_role)) {
         return [
            { value: '0', name: 'None' },
            { value: 'prepaid1_MO', name: '3 Months' },
            { value: 'prepaid2_MO', name: '4 Months' },
            { value: 'prepaid3_MO', name: '6 Months' },
            { value: 'prepaid4_MO', name: '9 Months' },
            { value: 'prepaid1', name: '1 Year' },
            { value: 'prepaid2', name: '2 Years' },
            { value: 'prepaid3', name: '3 Years' },
         ]
      } else {
         return [
            { value: '0', name: 'None' },
            { value: 'prepaid1', name: '1 Year' },
            { value: 'prepaid2', name: '2 Years' },
            { value: 'prepaid3', name: '3 Years' },
         ]
      }
   }

   /////////////////////////////////////////////////
   // Fetch Payroll Info Records from Backend
   /////////////////////////////////////////////////
   useEffect(() => {
      ;(async () => {
         if (opportunity_id) {
            // @ts-ignore: Original File is in js
            const { status, records, error } = await fetchPayrollInfoRecords(
               opportunity_id,
               host_url,
            )
            if (status === 200) {
               setPayrollInfoRecords(records || [])
            } else {
               const error_message =
                  error ||
                  'Unable to fetch previously cloned EINs from the database.'
               console.error(error_message)
               toast.error(error_message, { duration: Infinity })
            }
         }
      })()
   }, [opportunity_id])

   /////////////////////////////////////////////////
   // Fetch Opportunity Product Records from Backend
   /////////////////////////////////////////////////
   useEffect(() => {
      ;(async () => {
         if (opportunity_id) {
            // @ts-ignore: Original File is in js
            const { status, products, error } = await fetchOpportunityProducts(
               opportunity_id,
               host_url,
            )
            if (status === 200) {
               setDynamicProducts(products || new Map())
            } else {
               const error_message =
                  error || 'Unable to fetch Opportunity Products.'
               console.error(error_message)
               toast.error(error_message, { duration: Infinity })
            }
         }
      })()
   }, [opportunity_id])

   /////////////////////////////////////////////////
   // Update locks when deal desk discounts change
   /////////////////////////////////////////////////
   useEffect(() => {
      if (Object.keys(discount_list).length === 0) return

      console.debug('[Deal Desk Locks] Calculating locks...')
      console.debug(
         '[Deal Desk Locks] dealDeskDiscountRecords:',
         dealDeskDiscountRecords.length,
      )
      console.debug('[Deal Desk Locks] Old rates:', {
         hr: discountDealDeskHRImpRate,
         quickstart: discountDealDeskQuickstartImpRate,
         payroll: discountDealDeskPayrollImpRate,
      })

      const allLocks = calculateDealDeskLocks(
         dealDeskDiscountRecords,
         discount_list,
         {
            hr_dealdesk_imp_discount_rate: discountDealDeskHRImpRate,
            quickstart_dealdesk_imp_discount_rate:
               discountDealDeskQuickstartImpRate,
            payroll_dealdesk_imp_discount_rate: discountDealDeskPayrollImpRate,
         },
      )

      console.debug('[Deal Desk Locks] Calculated locks:', Array.from(allLocks))

      dealDeskLocks.forEach((lock) => removeDealDeskLock(lock))
      allLocks.forEach((lock) => addDealDeskLock(lock))
   }, [dealDeskDiscountRecords])

   /////////////////////////////////////////////////
   // Sync deal desk discount records into discount_list
   /////////////////////////////////////////////////
   // Track whether discount_list has been populated to trigger re-sync
   const discountListPopulated = Object.keys(discount_list).length > 0

   useEffect(() => {
      // Wait until discount_list is populated before syncing
      if (!discountListPopulated) return

      // Convert current deal desk discount records to standard Discount format
      const converted: Record<string, Discount> = {}
      dealDeskDiscountRecords.forEach((ddDiscount) => {
         const standardDiscount = convertDealDeskDiscountToStandardFormat(
            ddDiscount,
            product_list,
         )
         converted[standardDiscount.DiscountCode] = standardDiscount
      })

      // Use functional updater to always operate on latest discount_list
      setdiscount_list((prev) => {
         // Remove all existing deal desk discounts from the list
         const cleaned = removeAllDealDeskDiscountsFromList(prev)

         // Merge cleaned list with converted deal desk discounts
         return {
            ...cleaned,
            ...converted,
         }
      })
   }, [dealDeskDiscountRecords, product_list, discountListPopulated])

   let opportunityType2Set

   // Set opportunity type based on record type
   useEffect(() => {
      if (accountStatus === 'Subscriber') {
         opportunityType2Set = 'Expansion'
      } else {
         opportunityType2Set = 'New'
      }
      setOpportunityType(opportunityType2Set)
   }, [recordType, accountStatus])

   // Handle opportunity data from useGetOpportunity hook
   useEffect(() => {
      ;(async () => {
         let excludedRoles, extendedLabelRoles
         if (featurePermissions && !isLoadingPermissions) {
            excludedRoles = featurePermissions?.OppLookUpExcludeRoles || []
            extendedLabelRoles =
               featurePermissions?.DiscountPrepaymentExtendedLabels || []
         }

         if (excludedRoles && !excludedRoles.includes(user_role)) {
            let parsed: Opportunity = opportunityData || {}

            if (opportunityError) {
               const error_message =
                  'Unable to connect to the database. While calculations will work, saving and loading quotes will not. Please create a Data Desk ticket at datadesk.bamboohr.net with "Quote App Database Issue" as the subject. Thank you.'
               console.error(error_message)
               toast.error(error_message, { duration: Infinity })
            }

            // set input states to parsed opp data
            if (parsed.book_id != null) setPrevPricebookId(parsed.book_id)
            else setPrevPricebookId(300)
            if (parsed.book_id != null) setPricebookId(parsed.book_id)
            parsed.quote_type
               ? setQuoteType(parsed.quote_type)
               : setQuoteType('full')
            // If expiration feature flag is set for user, set the expiration to 05-31-2025, else use what is saved in the db
            if (
               featurePermissions['DefaultExpirationTo05-31-2024']?.includes(
                  user_role,
               )
            )
               setQuoteExpirationDate(
                  new Date('2025-05-31').toISOString().split('T')[0],
               )
            else if (parsed.expiration_date != null)
               setQuoteExpirationDate(parsed.expiration_date.split('T')[0])

            // If no previously saved quote exists, use usage data. Else, use the saved quote.
            if (Object.keys(parsed).length === 0) {
               // If usage data exists, load it into the quote
               // Try to parse the account_snowflake data. If it fails, it is not a valid JSON object.
               const accountSnowflakeParam =
                  searchParams.get('account_snowflake')
               const decodedParam = accountSnowflakeParam
                  ? decodeURIComponent(accountSnowflakeParam)
                  : null
               let account_snowflake: AccountSnowflakeData = {}

               if (decodedParam) {
                  try {
                     // First try simple JSON parsing
                     account_snowflake = JSON.parse(decodedParam)
                  } catch (error) {
                     // If that fails, try decompressing
                     account_snowflake = JSON.parse(
                        decompressJsonForBrowser(decodedParam),
                     )
                  }
               }

               // Store account_snowflake in state so pricing adjustments useEffect can access it
               setAccountSnowflake(account_snowflake)
               setAccountSnowflakeLoaded(true)

               // parses and overrides formatting for EINs__c. Sample of what original EINs__c prop looks like coming from SF: "[[123,456,789],['Company 1','Company 2','Company 3'],[1117.75,89,88.98],[263,4,6],[63,7,14],[10,2,3]]"
               let parsedAccountSnowflakeEINs__c: Array<Array<string>> = []
               if (typeof account_snowflake.EINs__c === 'string') {
                  parsedAccountSnowflakeEINs__c = parseEINsData(
                     account_snowflake.EINs__c,
                  )
               }
               // @ts-ignore
               account_snowflake.EINs__c = parsedAccountSnowflakeEINs__c
               setAccountSnowflake(account_snowflake)
               const urlSearchString = window.location.search
               const params = new URLSearchParams(urlSearchString)
               params.get('record_type')
               if (params.get('record_type') != EOR_RECORD_TYPE_ID) {
                  if (
                     account_snowflake != null &&
                     Object.keys(account_snowflake).length > 0
                  ) {
                     // Set default core product based on account_snowflake data
                     if (account_snowflake?.Lead_Package__c) {
                        setCoreProductSelected(
                           account_snowflake.Lead_Package__c.split(',')[0],
                        )
                     } else if (account_snowflake.Billed_Core__c != null) {
                        setCoreProductSelected('core1')
                     } else if (account_snowflake.Billed_Pro__c != null) {
                        setCoreProductSelected('core2')
                     } else if (account_snowflake.Billed_Elite__c != null) {
                        setCoreProductSelected('core0')
                     }

                     // Set EE counts based on account_snowflake data
                     if (account_snowflake.Billed_Core__c != null) {
                        setProductCoreEECount(account_snowflake.Billed_Core__c)
                     } else if (account_snowflake.Billed_Pro__c != null) {
                        setProductCoreEECount(account_snowflake.Billed_Pro__c)
                     } else if (account_snowflake.Billed_Elite__c != null) {
                        setProductCoreEECount(account_snowflake.Billed_Elite__c)
                     }

                     if (account_snowflake.Billed_Performance__c != null) {
                        setProductPerformanceEECount(
                           account_snowflake.Billed_Performance__c,
                        )
                     }
                     if (account_snowflake.Billed_Time_Tracking__c != null) {
                        setProductTimeTrackingEECount(
                           account_snowflake.Billed_Time_Tracking__c,
                        )
                     }
                     if (account_snowflake.Billed_Ben_Admin__c != null) {
                        setProductBenefitsAdministrationEECount(
                           account_snowflake.Billed_Ben_Admin__c,
                        )
                     }
                     if (account_snowflake.Billed_Payroll__c != null) {
                        setProductPayrollEECount(
                           account_snowflake.Billed_Payroll__c,
                        )
                     }
                     if (
                        account_snowflake.Billed_Payroll__c != null ||
                        account_snowflake.Billed_Ben_Admin__c
                     ) {
                        setHasUSEmployees(true)
                     }

                     if (parsedAccountSnowflakeEINs__c != null) {
                        // Sort the EINs__c array based on the EIN numbers (first sub-array)
                        // We need to maintain relationships between all arrays, so we create indexes first
                        if (
                           Array.isArray(parsedAccountSnowflakeEINs__c) &&
                           parsedAccountSnowflakeEINs__c.length > 0 &&
                           Array.isArray(parsedAccountSnowflakeEINs__c[0])
                        ) {
                           const indexes = Array.from(
                              {
                                 length:
                                    parsedAccountSnowflakeEINs__c[0].length,
                              },
                              (_, i) => i,
                           )

                           // Sort the indexes based on EIN numbers (converting to numbers for proper numeric sorting)
                           indexes.sort((a, b) => {
                              const valA = Number(
                                 parsedAccountSnowflakeEINs__c[0][a],
                              )
                              const valB = Number(
                                 parsedAccountSnowflakeEINs__c[0][b],
                              )
                              return valA - valB
                           })

                           // Reorder all sub-arrays based on the sorted indexes
                           parsedAccountSnowflakeEINs__c =
                              parsedAccountSnowflakeEINs__c.map((subArray) => {
                                 if (Array.isArray(subArray)) {
                                    return indexes.map(
                                       (index) => subArray[index],
                                    )
                                 }
                                 return subArray
                              })
                        }

                        setProductPayrollEINCount(
                           parsedAccountSnowflakeEINs__c?.[0]?.length ?? 0,
                        )
                        setProductPayrollEINNames(
                           parsedAccountSnowflakeEINs__c?.[1] ?? [],
                        )
                        setProductPayrollEECounts(
                           parsedAccountSnowflakeEINs__c?.[4]?.map((value) =>
                              Math.round(parseFloat(value)),
                           ) ?? [],
                        )
                        setProductPayrollStateCounts(
                           parsedAccountSnowflakeEINs__c?.[6]?.map((value) =>
                              Math.round(parseFloat(value)),
                           ) ?? [],
                        )

                        // If the EIN already exists (already has billed employees), set the implementation type to 'none' on first load.
                        let temp_ein_imp_types = []
                        for (
                           let i = 0;
                           i < parsedAccountSnowflakeEINs__c?.[4]?.length;
                           i++
                        ) {
                           temp_ein_imp_types.push(
                              //@ts-ignore
                              parsedAccountSnowflakeEINs__c?.[4][i] > 0
                                 ? 'none'
                                 : 'full',
                           )
                        }
                        setProductPayrollEINImpTypes(temp_ein_imp_types)
                     }

                     // Select discounts automatically from Account Snowflake Data if present and meets feature flag.
                     // A 1 in various account_snowflake fields below means that they have a discount multiplier of 1 (no discount) A decimals means they have a discount.
                     if (
                        featurePermissions[
                           'SetSupportDefaultsOnFirstLoad'
                        ]?.includes(user_role)
                     ) {
                        if (
                           account_snowflake.Non_Profit_Discount_Previous__c &&
                           account_snowflake.Non_Profit_Discount_Previous__c !=
                              1
                        ) {
                           setDiscountNonProfit(true)
                        }
                        if (
                           account_snowflake.Related_Entity_Discount_Previous__c &&
                           account_snowflake.Related_Entity_Discount_Previous__c !=
                              1
                        ) {
                           setDiscountMultiEntity(true)
                        }
                        if (
                           account_snowflake.Reseller_Discount_Previous__c &&
                           account_snowflake.Reseller_Discount_Previous__c != 1
                        ) {
                           toast(
                              'This account is a Reseller account. Please direct this quote to the Reseller team.',
                              {
                                 icon: '⚠️',
                                 id: 'reseller-toast',
                                 duration: 10000,
                              },
                           )
                        }
                        if (
                           // @ts-ignore
                           (account_snowflake.Billed_Core__c! ||
                              account_snowflake.Billed_Pro__c ||
                              account_snowflake.Billed_Elite__c) > 0 &&
                           // @ts-ignore
                           account_snowflake.Billed_Payroll__c > 0 &&
                           // @ts-ignore
                           account_snowflake.Billed_Ben_Admin__c > 0
                        ) {
                           setDiscountBundle(true)
                        }
                     }
                  }
               } // This closes the if (recordType != 'EOR') block

               // Unsaved opportunity (no row in `opportunity` table): seed both
               // dates directly so the render gate (activationDate != null &&
               // nextInvoiceDate != null) satisfies on first render without
               // depending on the secondary NID re-derivation effect.
               // Prior to this branch, the saved-opp `else` below was the only
               // path that set these, which left unsaved opps stranded on
               // LoadingScreen forever.
               if (
                  account_snowflake?.Next_Invoice_Date__c ||
                  timeData?.currentDate
               ) {
                  const {
                     activationDate: initialAD,
                     nextInvoiceDate: initialNID,
                  } = resolveInitialDates(
                     null,
                     null,
                     account_snowflake?.Next_Invoice_Date__c,
                     timeData?.currentDate,
                     false,
                  )
                  setActivationDate(initialAD)
                  setNextInvoiceDate(initialNID)
               }
            } //
            else {
               if (parsed.core_selected != null)
                  setCoreProductSelected(parsed.core_selected)
               if (parsed.time_product_selected != null)
                  setTimeProductSelected(parsed.time_product_selected)
               if (parsed.core_count != null)
                  setProductCoreEECount(parsed.core_count)
               if (parsed.performance_management_count != null)
                  setProductPerformanceEECount(
                     parsed.performance_management_count,
                  )
               if (parsed.time_tracking_count != null)
                  setProductTimeTrackingEECount(parsed.time_tracking_count)
               if (parsed.has_time_clocks != null)
                  setProductHasTimeClocks(parsed.has_time_clocks)
               if (parsed.time_clock_details != null)
                  setProductTimeClockDetails(parsed.time_clock_details)
               if (parsed.benefits_administration_count != null)
                  setProductBenefitsAdministrationEECount(
                     parsed.benefits_administration_count,
                  )
               if (parsed.us_employees != null)
                  setHasUSEmployees(parsed.us_employees)
               if (parsed.is_ats_only != null) setIsATSOnly(parsed.is_ats_only)

               if ('imp_type' in parsed)
                  parsed.imp_type === 'null' || !parsed.imp_type
                     ? setImpType('selfSetup')
                     : setImpType(parsed.imp_type)
               if ('ben_admin_imp_type' in parsed)
                  parsed.ben_admin_imp_type === 'null' ||
                  !parsed.ben_admin_imp_type
                     ? setBenAdminImpType('none')
                     : setBenAdminImpType(parsed.ben_admin_imp_type)
               if ('payroll_imp_type' in parsed) {
                  if (
                     parsed.payroll_imp_type === 'null' ||
                     parsed.payroll_imp_type === 'none'
                  ) {
                     setPayrollImpType('none')
                  } else if (parsed.payroll_imp_type === 'foundations') {
                     setPayrollImpType('foundations')
                  } else if ((parsed.payroll_count || 0) > 0) {
                     setPayrollImpType('full')
                  } else {
                     setPayrollImpType(parsed.payroll_imp_type!)
                  }
               }
               if (parsed.bundle_discount != null)
                  setDiscountBundle(parsed.bundle_discount)
               if (parsed.nonprofit_discount != null)
                  setDiscountNonProfit(parsed.nonprofit_discount)
               if (parsed.term_length != null)
                  setTermLength(Number(parsed.term_length))
               // NEW: Parse prepay_length from opportunity
               if (parsed.prepay_length != null)
                  setPrepayLength(Number(parsed.prepay_length))
               if (parsed.annual_prepayment_discount != null) {
                  let prepaymentOptions: SimpleOption[] =
                     discountPrepaymentDataSaved || []
                  if (prepaymentOptions.length === 0) {
                     // Check if prepaymentOptions is not empty from the outer useEffect if so, it will set the DiscountData here
                     prepaymentOptions = getDiscountPrepaymentData(
                        extendedLabelRoles || [],
                     )
                  }
                  prepaymentOptions.some(
                     (option) =>
                        option.value === parsed.annual_prepayment_discount,
                  )
                     ? setDiscountPrepaymentYears(
                          parsed.annual_prepayment_discount,
                       )
                     : setDiscountPrepaymentYears('0')
               }
               if (parsed.multi_entity_discount != null)
                  setDiscountMultiEntity(parsed.multi_entity_discount)
               if (parsed.hr_promotion_monthly_discount != null)
                  setDiscountHRMonthlyPromotion(
                     parsed.hr_promotion_monthly_discount,
                  )
               if (parsed.hr_dealdesk_monthly_discount != null)
                  setDiscountHRDealDeskMonthly(
                     parsed.hr_dealdesk_monthly_discount,
                  )
               if (parsed.reseller_discount != null)
                  setDiscountReseller(parsed.reseller_discount)
               if (parsed.quickstart_training_discount != null)
                  setDiscountQuickstartTraining(
                     parsed.quickstart_training_discount,
                  )
               if (parsed.promotion_imp_discount != null)
                  setDiscountImpPromotion(parsed.promotion_imp_discount)
               if (parsed.hr_dealdesk_imp_discount != null)
                  setDiscountDealDeskHRImp(parsed.hr_dealdesk_imp_discount)
               if (parsed.payroll_dealdesk_imp_discount != null)
                  setDiscountDealDeskPayrollImp(
                     parsed.payroll_dealdesk_imp_discount,
                  )
               if (parsed.second_ein_discount != null)
                  setDiscountSecondEIN(parsed.second_ein_discount)
               if (parsed.group_imp_discount != null)
                  setDiscountGroupImp(parsed.group_imp_discount)
               // If dealdesk rates are overriden in the DB, set the rates and the discount
               if (parsed.hr_dealdesk_imp_discount_rate != null) {
                  setDiscountDealDeskHRImpRate(
                     parsed.hr_dealdesk_imp_discount_rate,
                  )
               }
               if (parsed.quickstart_dealdesk_imp_discount_rate != null) {
                  setDiscountDealDeskQuickstartImpRate(
                     parsed.quickstart_dealdesk_imp_discount_rate,
                  )
               }
               if (parsed.payroll_dealdesk_imp_discount_rate != null) {
                  setDiscountDealDeskPayrollImpRate(
                     parsed.payroll_dealdesk_imp_discount_rate,
                  )
               }
               if (parsed.deal_desk_locks) {
                  setDealDeskLocks(new Set(parsed.deal_desk_locks))
               }
               if (parsed.additional_storage_count != null)
                  setProductAdditionalStorageCount(
                     parsed.additional_storage_count,
                  )
               if (parsed.additional_job_count != null)
                  setProductAdditionalJobsCount(parsed.additional_job_count)
               if (parsed.historical_employee_import != null)
                  setProductHistoricalDataImport(
                     parsed.historical_employee_import,
                  )
               if (parsed.marketplace_listing != null)
                  setMarketplaceListing(parsed.marketplace_listing)
               if (parsed.sponsorship != null)
                  setProductSponsorship(parsed.sponsorship)
               if (parsed.paid_service != null)
                  setProductPaidServiceAmount(parsed.paid_service)
               if (parsed.quote_notes != null)
                  try {
                     setQuoteNotes(atob(parsed.quote_notes))
                  } catch {
                     setQuoteNotes(parsed.quote_notes)
                  }
               if (parsed.test_account != null)
                  setProductTestAccount(parsed.test_account)
               if (parsed.payroll_first_pay_date != null)
                  setPayrollFirstPayDate(
                     parsed.payroll_first_pay_date.split('T')[0],
                  )
               if (parsed.is_co_term != null) setIsCoTerm(parsed.is_co_term)
               if (parsed.core_country_count != null)
                  setCoreCountryCount(parsed.core_country_count)
               if (parsed.account_balance != null)
                  setAccountBalance(parsed.account_balance)
               if (parsed.monthly_tax != null) setMonthlyTax(parsed.monthly_tax)
               if (parsed.professional_service_details != null)
                  setProfessionalServiceDetails(
                     parsed.professional_service_details,
                  )
               if (parsed.consulting_service_details != null)
                  setConsultingServiceDetails(parsed.consulting_service_details)

               // Sets currency code if it exists
               if (parsed.currency_code != null) {
                  setCurrencyCode(parsed.currency_code)
               }
               if (parsed.ein_count != null)
                  setProductPayrollEINCount(parsed.ein_count)
               if (parsed.ein_names && Array.isArray(parsed.ein_names)) {
                  setProductPayrollEINNames(parsed.ein_names)
               } else {
                  try {
                     const accountSnowflakeParam =
                        searchParams.get('account_snowflake')
                     const decodedParam = accountSnowflakeParam
                        ? decodeURIComponent(accountSnowflakeParam)
                        : null
                     let account_snowflake: AccountSnowflakeData = {}

                     if (decodedParam) {
                        try {
                           // First try simple JSON parsing
                           account_snowflake = JSON.parse(decodedParam)
                        } catch (error) {
                           // If that fails, try decompressing
                           account_snowflake = JSON.parse(
                              decompressJsonForBrowser(decodedParam),
                           )
                        }
                     }
                     setProductPayrollEINNames(
                        // @ts-ignore
                        account_snowflake?.EINs__c?.[1] || [],
                     )
                  } catch (error) {
                     console.error('Error parsing account_snowflake:', error)
                     setProductPayrollEINNames([])
                  }
               }
               if (
                  parsed.ein_ee_counts &&
                  Array.isArray(parsed.ein_ee_counts)
               ) {
                  if (parsed.ein_ee_counts.constructor === Array) {
                     setProductPayrollEECounts(parsed.ein_ee_counts)
                     // Slices the array to the length of the ein count, then reduces the array to the total number of employees
                     if (
                        parsed.ein_ee_counts.length >= (parsed.ein_count || 0)
                     ) {
                        // if the array is longer than the ein count, slice it to the ein count
                        const trimedEECounts = parsed.ein_ee_counts.slice(
                           0,
                           parsed.ein_count || 0,
                        )
                        setProductPayrollEECount(
                           trimedEECounts.reduce((total, a) => total + a, 0),
                        )
                     } else {
                        // if the array is shorter than the ein count, reduce the array to the total number of employees
                        setProductPayrollEECount(
                           parsed.ein_ee_counts.reduce(
                              (total, a) => total + a,
                              0,
                           ),
                        )
                     }
                  } else {
                     setProductPayrollEECount(0)
                     setProductPayrollEECounts([])
                  }
               }
               if (
                  parsed.ein_state_counts &&
                  Array.isArray(parsed.ein_state_counts)
               ) {
                  if (parsed.ein_state_counts.constructor === Array) {
                     setProductPayrollStateCounts(parsed.ein_state_counts)
                     // Slices the array to the length of the ein count, then reduces the array to the total number of states
                     const trimedStateCounts = parsed.ein_state_counts.slice(
                        0,
                        parsed.ein_count || 0,
                     )
                     // Subtract 1 from each EIN's state count (first state per EIN is included in base payroll price)
                     let totalState = trimedStateCounts.reduce(
                        (total, a) => (a > 0 ? total + a - 1 : total + a),
                        0,
                     )
                     setProductPayrollTotalStateCount(totalState)
                  } else {
                     setProductPayrollStateCounts([])
                     setProductPayrollTotalStateCount(0)
                  }

                  if (
                     parsed.ein_imp_types &&
                     Array.isArray(parsed.ein_imp_types)
                  )
                     setProductPayrollEINImpTypes(parsed.ein_imp_types)
                  else setProductPayrollEINImpTypes([])
               }
               if (parsed?.ein_integrations) {
                  if (Array.isArray(parsed.ein_integrations)) {
                     setProductEINIntegrations(parsed.ein_integrations)
                     setEinIntegrationsCount(
                        parsed.ein_integrations.reduce(
                           (acc, curr) => acc + curr.length,
                           0,
                        ),
                     )
                  } else {
                     setProductEINIntegrations([])
                     setEinIntegrationsCount(0)
                  }
               }
               if (parsed?.deal_desk_locks) {
                  if (parsed.deal_desk_locks.constructor === Array) {
                     const deal_desk_locks_decoded = new Set(
                        parsed.deal_desk_locks,
                     )
                     setDealDeskLocks(deal_desk_locks_decoded)
                  } else {
                     console.error('Error parsing deal desk locks')
                  }
               }
               if (parsed?.prepayment_activation_use_difference) {
                  setPrepaymentActivationUseDifference(
                     parsed.prepayment_activation_use_difference,
                  )
               }
            }

            setDifferenceQuote(
               resolveInitialDifferenceQuote(
                  parsed?.difference_quote,
                  hasPermission('DifferenceQuoteDefaultOn', user_role, false),
               ),
            )

            // Get account_snowflake from query string IF NOT CLOSED. If not present or closed, try and get from db.
            // Prioritize parsed.stage if it's "booked", otherwise use search params
            let stage
            if (parsed?.stage === 'booked') {
               stage = 'booked'
            } else {
               stage = searchParams.get('stage')
               if (stage != null) stage = stage.toLowerCase()
            }
            // Update stage state with the determined value
            setstage(stage || '')
            if (stage == 'booked') {
               setIsClosed(true)
            }
            // Parse account_snowflake from URL params (preferred) or saved DB record
            let account_snowflake: AccountSnowflakeData | null = null

            if (
               !(stage == 'closed lost' || stage == 'closed won') &&
               searchParams.get('account_snowflake')
            ) {
               try {
                  const accountSnowflakeParam: string | null =
                     searchParams.get('account_snowflake')
                  const decodedParam = accountSnowflakeParam
                     ? decodeURIComponent(accountSnowflakeParam)
                     : null
                  let account_snowflake_parsed: AccountSnowflakeData = {}

                  if (decodedParam) {
                     try {
                        // First try simple JSON parsing
                        account_snowflake_parsed = JSON.parse(decodedParam)
                     } catch (error) {
                        // If that fails, try decompressing
                        account_snowflake_parsed = JSON.parse(
                           decompressJsonForBrowser(decodedParam),
                        )
                     }
                  }

                  console.debug(
                     '[App] account_snowflake:',
                     account_snowflake_parsed,
                  )
                  // parses and overrides formatting for EINs__c. Sample of what original EINs__c prop looks like coming from SF: "[[123,456,789],['Company 1','Company 2','Company 3'],[1117.75,89,88.98],[263,4,6],[63,7,14],[10,2,3]]"
                  //@ts-ignore
                  account_snowflake_parsed.EINs__c = parseEINsData(
                     account_snowflake_parsed.EINs__c || '',
                  )
                  account_snowflake = account_snowflake_parsed
                  setAccountSnowflake(account_snowflake)
                  setAccountSnowflakeLoaded(true)
               } catch (error) {
                  console.error(error)
               }
            } else if (parsed?.account_snowflake) {
               account_snowflake = parsed.account_snowflake
               setAccountSnowflake(account_snowflake)
               setAccountSnowflakeLoaded(true)
            } else {
               // No account_snowflake data available - mark as loaded anyway so pricing adjustments query doesn't wait forever
               setAccountSnowflakeLoaded(true)
            }

            // Use Current Month Invoice Currency (if available) or Last Month Invoice Currency if available
            if (account_snowflake?.CMI_Local_Currency__c)
               setPreviousCurrencyCode(account_snowflake.CMI_Local_Currency__c)
            else if (account_snowflake?.LMI_Local_Currency__c)
               setPreviousCurrencyCode(account_snowflake.LMI_Local_Currency__c)

            // Resolve initial dates from saved DB values, Snowflake NID, or today.
            // Guard: only resolve when timeData is ready or a non-null date source exists,
            // so we don't set null dates on the first effect fire before timeData loads.
            if (
               timeData?.currentDate ||
               parsed.activation_date ||
               !!parsed.next_invoice_date ||
               account_snowflake?.Next_Invoice_Date__c
            ) {
               const isFrozen = isFrozenStage(stage ?? '')
               const {
                  activationDate: initialAD,
                  nextInvoiceDate: initialNID,
               } = resolveInitialDates(
                  parsed.activation_date,
                  parsed.next_invoice_date,
                  account_snowflake?.Next_Invoice_Date__c,
                  timeData?.currentDate,
                  isFrozen,
               )
               setActivationDate(initialAD)
               setNextInvoiceDate(initialNID)
            }

            // Get promo_utm from query string. If not present, try and get from db.
            if (searchParams.get('promo_utm'))
               setPromoUTM(
                  decodeURIComponent(searchParams.get('promo_utm') || ''),
               )
            else if (parsed.promo_utm != null)
               setPromoUTM(decodeURIComponent(parsed.promo_utm))

            // Get promotional_discount from query string. If not present, try and get from db.
            if (searchParams.get('promotional_discounts')) {
               try {
                  let promotional_discounts = JSON.parse(
                     decodeURIComponent(
                        searchParams.get('promotional_discounts') || '',
                     ),
                  )
                  recordUserSetFamilyKeys(promotional_discounts)
                  setPromotionalDiscounts(promotional_discounts)
               } catch {
                  console.log(
                     'account snowflake in searchparams unable to be parsed:',
                     searchParams.get('promotional_discounts'),
                  )
               }
            } else if (parsed.promotional_discounts != null) {
               recordUserSetFamilyKeys(parsed.promotional_discounts)
               setPromotionalDiscounts(parsed.promotional_discounts)
            }

            // Get account_flags from query string. If not present, try and get from db.
            if (searchParams.get('account_flags'))
               setAccountFlags(searchParams.get('account_flags'))
            else if (parsed.account_flags != null)
               setAccountFlags(parsed.account_flags)

            // Get partner_account from query string. If not present, try and get from db.
            if (searchParams.get('partner_account'))
               setPartnerAccount(searchParams.get('partner_account'))
            else if (parsed.partner_account != null)
               setPartnerAccount(parsed.partner_account)

            // Get region from query string. If not present, try and get from db.
            if (searchParams.get('region'))
               setRegion(searchParams.get('region'))
            else if (parsed.region != null) setRegion(parsed.region)
         }
         // To render the main app section, PrevPricebookId must be set. Sets to the default pricebook if a reseller.
         else {
            setPrevPricebookId(200)
            if (timeData) {
               setNextInvoiceDate(timeData.currentDate)
               setActivationDate(timeData.currentDate)
            }
         }
      })()
   }, [
      opportunityData,
      opportunityError,
      featurePermissions,
      isLoadingPermissions,
      user_role,
      timeData,
      searchParams,
   ])

   // Re-derive NID when the snowflake NID, stage, activationDate, or currentDate
   // changes. Frozen quotes are skipped — their NID was set from the DB on load
   // and must not change.
   //
   // Stability: bail out if every input that could produce a non-null NID is
   // missing. Setting NID to null here can desync the main render gate and
   // strand the app on LoadingScreen, so we'd rather leave the previous value
   // in place than overwrite it with null.
   useEffect(() => {
      if (isFrozenStage(stage ?? '')) return
      const snowflakeNid = accountSnowflake?.Next_Invoice_Date__c
      const currentDate = timeData?.currentDate
      if (!snowflakeNid && !activationDate && !currentDate) return
      const next = resolveNextInvoiceDate({
         isFrozen: false,
         savedDbNid: null,
         snowflakeNid,
         activationDate,
         currentDate,
      })
      if (next != null) setNextInvoiceDate(next)
   }, [
      accountSnowflake?.Next_Invoice_Date__c,
      stage,
      timeData?.currentDate,
      activationDate,
   ])

   // Populates currency objects with the currency code from the query parameters and USD.
   useEffect(() => {
      // Skip if necessary data is missing
      if (!pricebookId || !currencyCode || !previousCurrencyCode || !pricebooks)
         return

      // Find the matching pricebook and get its currency
      const pricebookDetails = pricebooks.find((pb) => pb.value == pricebookId)
      const current = pricebookDetails?.currencyCode ?? 'USD'
      // Update the current currency code
      setCurrencyCode(current)

      // Set currency object from hook data
      if (currencyData) {
         setCurrencyObject(currencyData)
      }

      // Set USD currency object
      if (currencyDataUSD) {
         setCurrencyObjectUSD(currencyDataUSD)
      }

      // Determine which currency object to use for previous currency
      if (previousCurrencyCode === 'USD' && currencyDataUSD) {
         setCurrencyObjectPrevious(currencyDataUSD)
      } else if (previousCurrencyCode === current && currencyData) {
         setCurrencyObjectPrevious(currencyData)
      } else if (currencyDataPrevious) {
         setCurrencyObjectPrevious(currencyDataPrevious)
      }
   }, [
      pricebookId,
      currencyCode,
      previousCurrencyCode,
      pricebooks,
      currencyData,
      currencyDataUSD,
      currencyDataPrevious,
   ])

   // Sync payrollImpType from productPayrollEINImpTypes whenever the per-EIN array changes.
   // This handles load paths (Snowflake, DB) that call setProductPayrollEINImpTypes directly
   // without going through onProductPayrollImpTypeChange, which can leave payrollImpType
   // as 'none' even when EINs have foundations/full types set.
   //
   // TODO: When the calculation engine is refactored, consider deprecating payrollImpType
   // entirely and having calculate.mjs consume productPayrollEINImpTypes[i] per-EIN instead
   // of a single top-level type. That would eliminate this sync requirement and make mixed
   // EIN imp types price correctly.
   useEffect(() => {
      if (
         productPayrollEINCount === 0 ||
         productPayrollEINImpTypes.length === 0
      )
         return
      if (productPayrollEINImpTypes.some((t) => t === 'full')) {
         setPayrollImpType('full')
      } else if (productPayrollEINImpTypes.some((t) => t === 'foundations')) {
         setPayrollImpType('foundations')
      }
   }, [productPayrollEINImpTypes, productPayrollEINCount])

   // If agent changes state count or EE count then check payroll implementation type
   useEffect(() => {
      const validStateCount = productPayrollStateCounts.every(
         (stateCount) => stateCount <= 3,
      )
      if (
         (productPayrollTotalEECount > 25 || !validStateCount) &&
         payrollImpType === 'foundations'
      ) {
         setPayrollImpType('full')
      }
   }, [productPayrollTotalEECount, productPayrollStateCounts])

   useEffect(() => {
      if (productPayrollEINCount > 0) {
         const trimedStateCounts = productPayrollStateCounts.slice(
            0,
            productPayrollEINCount,
         )
         const totalState = trimedStateCounts.reduce(
            (total, a) => (a > 0 ? total + a - 1 : total + a),
            0,
         )
         setProductPayrollTotalStateCount(totalState)
      }
   }, [productPayrollEINCount])

   /**
    * Calls the calculateQuote function with all necessary parameters and returns the result
    *
    * @returns {Object} - Object containing all calculated values
    */
   function getCalculatedValues() {
      return calculateQuote(
         product_list,
         discount_list,
         pricebookId,
         quoteType,
         coreProductSelected,
         productCoreEECount,
         productPerformanceEECount,
         productTimeTrackingEECount, // Number of employees for time tracking
         productHasTimeClocks, // Boolean for time clocks
         productTimeClockDetails, // Array of time clock details
         productBenefitsAdministrationEECount,
         productPayrollEINCount, // Number of EINs for payroll
         productPayrollTotalEECount, // Has the total number of employees for all EINs
         productPayrollEECounts, // Array of EE counts for each EIN
         productPayrollTotalStateCount, // Has the total number of states for all EINs
         productPayrollStateCounts, // Array of state counts for each EIN
         professionalServiceDetails, // Professional services details
         consultingServiceDetails, // Consulting services details
         impType,
         benAdminImpType,
         payrollImpType,
         discountBundle,
         discountNonProfit,
         prepayLength, // Prepayment length in months (replaces discountPrepaymentYears)
         discountMultiEntity,
         discountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         discountReseller,
         discountQuickstartTraining,
         discountImpPromotion,
         discountDealDeskHRImp,
         discountDealDeskPayrollImp,
         discountSecondEIN,
         discountGroupImp,
         discountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         productAdditionalJobsCount,
         productHistoricalDataImport,
         productMarketplaceListing,
         productSponsorship,
         productPaidServiceAmount,
         productTestAccount,
         activationDate,
         differenceQuote,
         accountSnowflake,
         coreCountryCount,
         promotionalDiscounts,
         nextInvoiceDate,
         currencyObject,
         accountBalance,
         monthlyTax,
         productEINIntegrations,
         featurePermissions,
         user_role,
         accountStatus,
         pricebookId !== null
            ? findObjectByKeyValue(pricebooks, 'value', pricebookId)
            : null,
         currencyObjectPrevious,
         productPayrollEINImpTypes,
         prepaymentActivationUseDifference,
         dynamicProducts, // dynamicProducts
         timeProductSelected, // selectedTimeProduct
         isATSOnly,
         false, // isRecalculation
      )
   }

   // Wait until product and discount data has loaded to calculate and render app
   if (
      Object.keys(discount_list).length != 0 &&
      !('errno' in discount_list) &&
      !('error' in discount_list) &&
      Object.keys(product_list).length != 0 &&
      !('error' in product_list) &&
      Object.keys(pricebooks).length != 0 &&
      prevPricebookId != null &&
      pricebookId != null &&
      activationDate != null &&
      nextInvoiceDate != null &&
      // Loading States
      !isLoadingTimeData &&
      (!opportunity_id || !isLoadingOpportunity) && // Only wait for opportunity data if opportunity_id is provided
      !isLoadingDiscounts && // Check for status of discount data
      !isLoadingProducts && // Check for status of product data
      !isLoadingPricebooks && // Check for status of pricebook data
      !isLoadingPrevProducts && // Check for status of previous pricebook data
      !isLoadingTermLengths && // Check for status of term length data
      (!opportunity_id || !isLoadingPrepayLengths) && // Only wait for prepay lengths if opportunity_id is provided
      (!opportunity_id || !isLoadingDealDeskDiscounts) && // Only wait for deal desk discounts if opportunity_id is provided
      // Check for status of currency data
      !isLoadingCurrency &&
      !isLoadingCurrencyUSD &&
      !isLoadingCurrencyPrevious
   ) {
      // Get calculated data — drop isATSOnly so the state value (provided
      // explicitly below) is the single source of truth in context.
      const { isATSOnly: _, ...calculatedValues } = getCalculatedValues()
      // Structure for context
      const QuoteContextStructure = {
         // input states, multi-select values, their update functions
         featurePermissions,
         integrationTypes,
         integrationSolutions,
         strings,
         getString,
         getFormattedString,
         product_list,
         setproduct_list,
         discount_list,
         termLengthsList,
         prepayLengthsList,
         setdiscount_list,
         opportunity_id,
         setopportunity_id,
         parent_opportunity_id,
         setparent_opportunity_id,
         account_name,
         setaccount_name,
         opportunity_name,
         setopportunity_name,
         opportunity_contact_name,
         setopportunity_contact_name,
         opportunity_contact_email,
         setopportunity_contact_email,
         last_agent_email,
         last_agent_name,
         user_role,
         setuser_role,
         user_roles,
         user_view,
         setuser_view,
         user_views,
         pricebookId,
         setPricebookId,
         prevPricebookId,
         setPrevPricebookId,
         prevProductList,
         pricebooks,
         coreProductSelected,
         setCoreProductSelected,
         onCoreOptionChange,
         productCoreEECount,
         setProductCoreEECount,
         onProductCoreEECountChange,
         onProductCoreInputBlur,
         productPerformanceEECount,
         onProductPerformanceEECountChange,
         timeProductSelected,
         setTimeProductSelected,
         onTimeProductOptionChange,
         productTimeTrackingEECount,
         onProductTimeTrackingEECountChange,
         productHasTimeClocks,
         setProductHasTimeClocks,
         productTimeClockDetails,
         setProductTimeClockDetails,
         productBenefitsAdministrationEECount,
         onProductBenefitsAdministrationEECountChange,
         professionalServiceDetails,
         setProfessionalServiceDetails,
         consultingServiceDetails,
         setConsultingServiceDetails,
         productPayrollEINCount,
         setProductPayrollEINCount,
         onProductPayrollEINCountChange,
         productPayrollTotalStateCount,
         setProductPayrollTotalStateCount,
         productPayrollEINNames,
         setProductPayrollEINNames,
         productPayrollEECounts,
         setProductPayrollEECounts,
         productPayrollStateCounts,
         setProductPayrollStateCounts,
         productPayrollEINImpTypes,
         setProductPayrollEINImpTypes,
         onProductPayrollImpTypeChange,
         productEINIntegrations,
         setProductEINIntegrations,
         einIntegrationsCount,
         productPayrollTotalEECount,
         setProductPayrollEECount,
         onProductPayrollEECountChange,
         onProductPayrollEINNameChange,
         onProductPayrollEINStateCountChange,
         onProductPayrollEINEECountChange,
         onProductPayrollEINEECountInputBlur,
         onProductEINIntegrationAdd,
         onProductEINIntegrationRemove,
         onIntegrationChange,
         impType,
         setImpType,
         impTypes,
         onImpOptionChange,
         benAdminImpType,
         setBenAdminImpType,
         benAdminImpTypes,
         onBenAdminImpOptionChange,
         payrollImpType,
         setPayrollImpType,
         payrollImpTypes,
         onPayrollImpOptionChange,
         onClear,
         hasUSEmployees,
         setHasUSEmployees,
         isATSOnly,
         setIsATSOnly,
         quoteExpirationDate,
         setQuoteExpirationDate,
         onExpirationChange,
         activationDate,
         setActivationDate,
         onActivationDateChange,
         payrollFirstPayDate,
         setPayrollFirstPayDate,
         onPayrollFirstPayDateChange,
         discountQuickstartTraining,
         setDiscountQuickstartTraining,
         discountBundle,
         setDiscountBundle,
         discountNonProfit,
         setDiscountNonProfit,
         discountPrepaymentYears,
         setDiscountPrepaymentYears,
         prepaymentDiscounts,
         setPrepaymentDiscounts,
         termLength,
         setTermLength,
         prepayLength,
         setPrepayLength,
         isCoTerm,
         setIsCoTerm,
         discountPrepaymentDataSaved,
         discountPrepaymentData,
         setDiscountPrepaymentData,
         discountHRMonthlyPromotion,
         setDiscountHRMonthlyPromotion,
         discountHRDealDeskMonthly,
         setDiscountHRDealDeskMonthly,
         discountReseller,
         setDiscountReseller,
         discountMultiEntity,
         setDiscountMultiEntity,
         discountSecondEIN,
         setDiscountSecondEIN,
         discountImpPromotion,
         setDiscountImpPromotion,
         discountDealDeskHRImp,
         setDiscountDealDeskHRImp,
         discountDealDeskPayrollImp,
         setDiscountDealDeskPayrollImp,
         discountGroupImp,
         setDiscountGroupImp,
         discountDealDeskHRImpRate,
         setDiscountDealDeskHRImpRate,
         discountDealDeskQuickstartImpRate,
         setDiscountDealDeskQuickstartImpRate,
         discountDealDeskPayrollImpRate,
         setDiscountDealDeskPayrollImpRate,
         productAdditionalStorageCount,
         onProductAdditionalStorageCountChange,
         productAdditionalJobsCount,
         onProductAdditionalJobsCountChange,
         productHistoricalDataImport,
         setProductHistoricalDataImport,
         productMarketplaceListing,
         setMarketplaceListing,
         productSponsorship,
         setProductSponsorship,
         productPaidServiceAmount,
         onProductPaidServiceAmountChange,
         quoteNotes,
         setQuoteNotes,
         productTestAccount,
         setProductTestAccount,
         pricebookSelectorList,
         setPricebookSelectorList,
         currencyCode,
         setCurrencyCode,
         previousCurrencyCode,
         setPreviousCurrencyCode,
         currencyObject,
         setCurrencyObject,
         currencyObjectPrevious,
         setCurrencyObjectPrevious,
         currencyObjectUSD,
         setCurrencyObjectUSD,
         quoteType,
         setQuoteType,
         differenceQuote,
         setDifferenceQuote,
         accountSnowflake,
         coreCountryCount,
         setCoreCountryCount,
         PromoUTM,
         setPromoUTM,
         accountFlags,
         setAccountFlags,
         promotionalDiscounts,
         setPromotionalDiscounts,
         recordUserSetFamilyKeys,
         nextInvoiceDate,
         setNextInvoiceDate,
         timeData,
         accountBalance,
         setAccountBalance,
         monthlyTax,
         setMonthlyTax,
         accountStatus,
         setAccountStatus,
         recordType,
         setRecordType,
         opportunityType,
         setOpportunityType,
         partnerAccount,
         region,
         getCalculatedValues,
         dynamicProducts,
         setDynamicProduct,
         stage,
         setstage,
         companyId,
         setCompanyId,
         // calculated fields
         ...calculatedValues,
         dealDeskLocks,
         addDealDeskLock,
         removeDealDeskLock,
         ignoreHRISImplementationDealDeskDiscountList,
         ignorePayrollImplementationDealDeskDiscountList,
         ignoreQuickstartDealDeskDiscountList,
         prepaymentActivationUseDifference,
         setPrepaymentActivationUseDifference,
         payrollInfoRecords,
         setPayrollInfoRecords,
         dealDeskDiscountRecords,
         setDealDeskDiscountRecords,
         isClosed,
         setIsClosed,
      }

      // Formatted HTML React App
      return (
         <div className='container' id='main'>
            <QuoteContext.Provider value={QuoteContextStructure}>
               <BookedQuoteNotification />
            </QuoteContext.Provider>
            <fieldset disabled={isClosed}>
               <QuoteContext.Provider value={QuoteContextStructure}>
                  <ToolbarSection
                     pricebookID={pricebookId}
                     setPricebookIDCallback={setPricebookId}
                     productPerformanceEECount={productPerformanceEECount}
                     setProductPerformanceEECount={setProductPerformanceEECount}
                     productTestAccount={productTestAccount}
                     setProductTestAccount={setProductTestAccount}
                  />
                  <BambooHRLogo />
                  <QuoteInfoRow />
                  <VolumeDiscountAlert />
                  <MonthlySection />
                  <OneTimeSection />
                  <ExpansionSection />
                  <Toaster
                     containerStyle={{
                        top:
                           (document?.getElementById('main')?.offsetHeight ??
                              0) / 3 || 0,
                        right: 0,
                     }}
                  />
                  {/* Discount and Totals Row */}
                  <div className='row'>
                     {/* Discount Section Column */}
                     <div className='col'>
                        <OtherOptionsSection />
                        {/* <MonthlyDiscountsSection />
                                <OneTimeDiscountsSection /> */}
                     </div>
                     {/* Totals Section Column */}
                     <div className='col'>
                        <TotalsSection
                           productPerformanceEECount={productPerformanceEECount}
                           setProductPerformanceEECount={
                              setProductPerformanceEECount
                           }
                           productTestAccount={productTestAccount}
                           setProductTestAccount={setProductTestAccount}
                        />
                        <ResellerSection
                           productPerformanceEECount={productPerformanceEECount}
                           setProductPerformanceEECount={
                              setProductPerformanceEECount
                           }
                           productTestAccount={productTestAccount}
                           setProductTestAccount={setProductTestAccount}
                        />
                     </div>
                  </div>
                  {environment == 'development' ||
                  user_role == 'Super Admin' ? (
                     <UserRoleAndViewSection />
                  ) : (
                     ''
                  )}
               </QuoteContext.Provider>
            </fieldset>
         </div>
      )
   }
   // If API call unauthorized, display unauthorized error.
   else if (authStatus == 403) {
      return <UnauthorizedScreen />
   } else if (Object.keys(pricebooks).length == 0) {
      return (
         <div className='container'>
            <NoPricebooksAssigned />
         </div>
      )
   }
   // If product and discount data not loaded, return loading page.
   else {
      return (
         <div className='container'>
            <LoadingScreen />
            <Toaster position='top-center' />
         </div>
      )
   }
}

export default App
